import type {
  CreateWantedProfileBody,
  UpdateWantedProfileBody,
  WantedProfile,
  WantedProfileIdParams,
  WantedProfileJobCodesParams,
  WantedProfileJobCodesResponse,
  WantedProfileListParams,
  WantedProfileListResponse,
} from "../schemas/wanted-profile";
import type { PatchContractBody } from "../schemas/contract/patch-contract";
import type { ContractStatusMutationResponse, ContractSubmissionResponse } from "../schemas/contract/contract-status";
import type { ContractCorrectionRequestBody } from "../schemas/contract/contract-correction";
import type {
  ContractDocument,
  ContractDocumentsQueryParams,
  LinkContractDocumentBody,
  LinkContractDocumentResponse,
} from "../schemas/contract/contract-documents";
import type {
  AddContractContactsBody,
  AddContractContactsParams,
  ContractContactsMutationResponse,
  ContractContactsResponse,
  CopyContractContactsBody,
  RemoveContractContactParams,
} from "../schemas/contract/contract-contacts";
import type { ContractDefinitiveEndMutationResponse } from "../schemas/contract/contract-definitive-end";
import type { ChangeContractOwnerBody, ContractOwnerMutationResponse } from "../schemas/contract/contract-owner";
import type { ContractDetail } from "../schemas/contract/detail";
import type { ContractKeyDetail } from "../schemas/contract/planning";
import type { TalentContractKeysResponse, TalentContractSummary } from "../schemas/contract/by-talent";
import type { AddPayTrackingDocumentBody } from "../schemas/pay-tracking/add-document.schema";
import type { GlobalSearchQuery, GlobalSearchResponse } from "../schemas/global-search";
import type { ImpersonateTalentBody, ImpersonateTalentResponse } from "../schemas/admin/impersonate-talent";
import type {
  TeamMemberContractPermissionsResult,
  TeamMemberExpenseClaimRequestPermissionsResult,
} from "../domain/permissions";
import type {
  GetClassifiableTalentCountsAnswer,
  GetClassifiableTalentsAnswer,
  GetClassifiableTalentsQuery,
  GetClassifiedTalentsAnswer,
  GetClassifiedTalentsQuery,
  GetJobMatrixTagsAnswer,
  GetJobMatrixTagsQuery,
  GetTalentClassificationTagsAnswer,
  UpdateTalentJobMatrixTrainClassificationBusinessUnitPayload,
  UpdateTalentJobMatrixTrainClassificationBusinessUnitResponse,
  ValidateTalentReferenceClassificationAnswer,
  ValidateTalentReferenceClassificationPayload,
} from "../schemas/job-matrix";
import type {
  FullDocument,
  DocumentLifecycleStatus,
  GetSocialSecurityCardNumberResponse,
} from "../schemas/document.schemas";
import type {
  CoverListResponse,
  CreditInsuranceAccessResponse,
  CreditInsurerRequestListParams,
  CreditInsurerRequestListResponse,
  CreditInsurerRequestResponse,
  RequestCoverPayload,
} from "../schemas/credit-insurance";
import type {
  CreateTalentReadyForContractBody,
  CreateTalentReadyForContractResponse,
} from "../schemas/seed-scenario/talent-seed-scenario.schema";
import type {
  CreateContractScenarioBody,
  CreateContractScenarioResponse,
} from "../schemas/seed-scenario/contract-seed-scenario.schema";
import type {
  AdvanceContractBody,
  AdvanceContractResponse,
  ListContractAdvanceStepsResponse,
} from "../schemas/dev-tools/contract-advance.schema";
import type { EnsureSeedPersonasResponse } from "../schemas/seed-scenario/persona-seed-scenario.schema";
import type {
  SimulateContractRefusalBody,
  SimulateContractSignatureBody,
  SimulateContractSignatureResponse,
} from "../schemas/dev-tools/contract-signature-simulation.schema";
import type { InternalReviewRejectionBody, InternalReviewRejectionResponse } from "../schemas/document-review.schemas";
import type {
  BulkExportTimesheetLinksQuery,
  CreateTimesheetByContractKeyBody,
  FindTimesheetByContractKeyParams,
  ListTimesheetsFromContactQuery,
  ListTimesheetsQuery,
  PldRhResponse,
  SingleTimesheetParams,
  TimesheetFile,
  TimesheetLink,
  TimesheetResetBody,
  TimesheetResetResponse,
  TimesheetResponse,
  TimesheetResponseSchemaWithFile,
  TimesheetSendToClientBody,
  TimesheetSendToClientResponse,
  TimesheetsResponse,
  CustomerTimesheetsResponse,
  UpdateClientProposalBody,
  UpdateTimesheetByContractKeyBody,
} from "../schemas/timesheet";
import type {
  CreateJobOfferResponse,
  DeleteJobOfferResponse,
  GetJobPostingResponse,
  JobPostingQuery,
  UpdateJobOfferResponse,
} from "../types/job-posting";
import type { ActionCenterCounts } from "../schemas/action-center";
import type { NavButtonCount } from "../types/navbar";
import type {
  QualificationTrainingRequestApproveQuery,
  QualificationTrainingRequestApproveResponse,
} from "../types/qualification-training-request";
import type { GeoCommunesQuery, GeoCommunesResponse } from "../schemas/geo/communes";
import type { PldClientCreateBody, PldClientCreateResponse, PldClientPreviewResponse } from "../schemas/pld/client";
import type {
  PldDocumentsPreviewResponse,
  PldDocumentsUploadBody,
  PldDocumentsUploadResponse,
} from "../schemas/pld/documents";
import type {
  PldInterimaireCreateBody,
  PldInterimaireCreateResponse,
  PldInterimairePreviewResponse,
  PldInterimaireUpdateBody,
  PldInterimaireUpdateResponse,
} from "../schemas/pld/interimaire";
import type {
  PldContractsListWithLatestResponse,
  PldContractCreateBody,
  PldContractCreateResponse,
  PldContractExtensionCreateBody,
  PldContractExtensionCreateResponse,
  PldContractPreviewQuery,
  PldContractPreviewResponse,
} from "../schemas/pld/contract";
import type { PldQualificationsListQuery, PldQualificationsListResponse } from "../schemas/pld/qualifications";
import type { PldCommercialsListQuery, PldCommercialsListResponse } from "../schemas/pld/commercials";
import type { PldConsignesListQuery, PldConsignesListResponse } from "../schemas/pld/consignes";
import type { PldPrimesListQuery, PldPrimesListResponse } from "../schemas/pld/primes";
import type { PldJobTriggerResponseSchema } from "../schemas/pld/job";
import type {
  PldExtensionAmendmentBatchPreviewResponseSchema,
  PldExtensionAmendmentBatchSendBodySchema,
} from "../schemas/pld/extension-amendment-batch";
import type {
  PldTimesheetBatchPreviewResponseSchema,
  PldTimesheetBatchSendBodySchema,
} from "../schemas/pld/timesheet-batch";

import type {
  ContractParams,
  ContractSchema,
  ContractsQueryParams,
  ContractsResponse,
  TalentContractToSignNotificationBody,
} from "../schemas/contract/list";
import type { ContractsByTalentParams, ContractsByTalentResponse } from "../schemas/contract/history";
import type {
  DocumentSenderContactBody,
  DocumentSenderTalentBody,
  HtmlEmailSenderBody,
  MedispaceEmailSenderBody,
  SmsSenderBody,
} from "../supabase/use-cases/sender-handler/schema";
import type { Logger } from "../logger";
import type {
  TeamMemberAbsenceBody,
  TeamMemberAbsenceResponse,
  TeamMemberAutonomyBody,
  TeamMemberOffboardingBody,
  TeamMemberOffboardingStatusQuery,
  TeamMemberOffboardingStatusResponse,
  TeamMemberRevertAbsenceBody,
  InviteTeamMemberBody,
  InviteTeamMemberResponse,
} from "../types/team-member";
import type { TeamMemberProfileResponse } from "../schemas/team-member/get-team-member.schema";
import type { ListTeamMembersQuery, ListTeamMembersResponse } from "../schemas/team-member/list-team-members.schema";
import type {
  TeamMemberOrganigramListParams,
  ListTeamMemberOrganigramResponse,
} from "../schemas/team-member/list-team-member-organigram";
import type { ErrorSerialized } from "../utils/error";
import { getErrorMessageForClient, SharedError } from "../utils/error";
import type { EmptyBodySchema } from "../schemas/shared";
import type {
  AdvancePaymentEligibilityResponse,
  AdvancePaymentFilesResponseSchema,
  AdvancePaymentListParamsSchema,
  AdvancePaymentListResponseSchema,
  AdvancePaymentRefuseBodySchema,
  AdvancePaymentSchema,
  AdvancePaymentSendToPldBodySchema,
  AdvancePaymentUpdateStatusBodySchema,
  PostAdvancePaymentRequestBodySchema,
  PostAdvancePaymentRequestResponseSchema,
} from "../schemas/advance-payment";
import type {
  Body as AnalyticsTalentIdentifyBody,
  Response as AnalyticsTalentIdentifyResponse,
} from "../schemas/analytics/talent/identify";
import type {
  Body as AnalyticsTalentTrackEventBody,
  Response as AnalyticsTalentTrackEventResponse,
} from "../schemas/analytics/talent/track-event";
import type {
  Body as AnalyticsTalentTrackPageViewBody,
  Response as AnalyticsTalentTrackPageViewResponse,
} from "../schemas/analytics/talent/track-page-view";
import type {
  Body as AnalyticsTeamMemberIdentifyBody,
  Response as AnalyticsTeamMemberIdentifyResponse,
} from "../schemas/analytics/team-member/identify";
import type {
  Body as AnalyticsTeamMemberTrackEventBody,
  Response as AnalyticsTeamMemberTrackEventResponse,
} from "../schemas/analytics/team-member/track-event";
import type {
  Body as AnalyticsTeamMemberTrackPageViewBody,
  Response as AnalyticsTeamMemberTrackPageViewResponse,
} from "../schemas/analytics/team-member/track-page-view";
import type {
  Company,
  CompanyInsertPayload,
  CompanyListParams,
  CompanyListResponse,
  CompanyRiskUpdatePayload,
  CompanyUpdatePayload,
} from "../schemas/company";
import type {
  Query as CompanyAutocompleteQuery,
  Response as CompanyAutocompleteResponse,
} from "../schemas/company-autocomplete";
import type { CompanySuggestionListQuery, CompanySuggestionListResponse } from "../schemas/company-suggestion";
import type { ContractCoefficientsListParams, ContractCoefficientsResult } from "../schemas/contract-coefficients/list";
import type {
  Body as CustomerioApplicationSyncBody,
  Response as CustomerioApplicationSyncResponse,
} from "../schemas/customerio/application/sync";
import type {
  Body as CustomerioContractSyncBody,
  Response as CustomerioContractSyncResponse,
} from "../schemas/customerio/contract/sync";
import type {
  Body as CustomerioMissionSyncBody,
  Response as CustomerioMissionSyncResponse,
} from "../schemas/customerio/mission/sync";
import type {
  Body as CustomerioTalentSyncBody,
  Response as CustomerioTalentSyncResponse,
} from "../schemas/customerio/talent/sync";
import type {
  DefinitiveEndRequestListParamsSchema,
  DefinitiveEndRequestListResponseSchema,
} from "../schemas/definitive-end-request";
import type {
  ExpenseClaimRequestListParamsSchema,
  ExpenseClaimRequestListResponseSchema,
  ExpenseClaimRequestFilesResponseSchema,
  ExpenseClaimRequestSchema,
  ExpenseClaimRequestCreateBodySchema,
  ExpenseClaimRequestCreateResponseSchema,
  ExpenseClaimRequestUpdateStatusBodySchema,
  ExpenseClaimRequestConfirmReviewBodySchema,
} from "../schemas/expense-claim-request";
import type { FeatureFlagQuery, FeatureFlagResponse, FeatureFlagsResponse } from "../schemas/feature-flag";
import type { FileOrphanResponseType } from "../schemas/file.schema";
import type { ParseResumeContactDetailsResponse } from "../schemas/resume/parse-contact-details";
import type {
  CreateTalentResumeParams,
  CreateTalentResumeResponse,
  DeleteTalentResumeParams,
  DeleteTalentResumeResponse,
} from "../schemas/resume/talent-resume";
import type { IbanExtractResponse } from "../schemas/iban/extract";
import type {
  AddTalentToHiringPoolBody,
  HiringPoolTableQueryParams,
  HiringPoolListQueryParams,
  HiringPoolPageListParams,
  HiringPool,
  HiringPoolListResponse,
  HiringPoolPageListResponse,
  DeleteHiringPoolResponse,
  CreateHiringPoolBody,
  UpdateHiringPoolBody,
  HiringPoolSearchQuery,
  RemoveTalentFromHiringPoolParams,
  TalentHiringPool,
} from "../schemas/hiring-pool";
import type {
  CreateSavedTalentSearchBody,
  DeleteSavedTalentSearchResponse,
  ListSavedTalentSearchesQuery,
  ListSavedTalentSearchesResponse,
  RecordSavedTalentSearchConsultationBody,
  RecordSavedTalentSearchConsultationResponse,
  SavedTalentSearchEmailAlertPreference,
  SavedTalentSearchEmailAlertPreferenceResponse,
  UpdateSavedTalentSearchBody,
  SavedTalentSearch,
} from "../schemas/saved-talent-search";
import type {
  CreateSmsRemindersBody,
  SmsIdParams,
  SmsOkResponse,
  SmsReminderListParams,
  SmsReminderListResponse,
  SmsSendResponse,
  UpdateSmsContentBody,
} from "../schemas/sms";
import type {
  SignatureRequestListParams,
  SignatureRequestListResponse,
  SignatureRequestResponse,
  SigningOptionsParams,
  SigningOptionsResponse,
} from "../schemas/signature-request";
import type {
  IndependentPaymentRequestListParamsSchema,
  IndependentPaymentRequestListResponseSchema,
} from "../schemas/independent-payment-request";
import type { Query as JobAlternativesQuery, Response as JobAlternativesResponse } from "../schemas/job/alternatives";
import type { Query as JobAutocompleteQuery, Response as JobAutocompleteResponse } from "../schemas/job/autocomplete";
import type { MealCardRequestListParamsSchema, MealCardRequestListResponseSchema } from "../schemas/meal-card-request";
import type { MedicalCenterListParamsSchema, MedicalCenterListResponseSchema } from "../schemas/medical-center";
import type {
  MedicalCheckupRequestListParamsSchema,
  MedicalCheckupRequestListResponseSchema,
} from "../schemas/medical-checkup-request";
import type {
  FollowMissionBody,
  FollowMissionParams,
  FollowMissionResponse,
  GetMissionFollowersParams,
  GetMissionFollowersResponse,
  UnfollowMissionParams,
  UnfollowMissionResponse,
} from "../schemas/mission/follower";
import type { MissionTableQueryParams, MissionTableRow } from "../schemas/mission/mission";
import type {
  Body as NoteGenerateTitleBody,
  Response as NoteGenerateTitleResponse,
} from "../schemas/note/generate-title";
import type {
  Body as NoteSummarizeContentBody,
  Response as NoteSummarizeContentResponse,
} from "../schemas/note/summarize-content";
import type {
  Body as NoteTranscribeAudioBody,
  Response as NoteTranscribeAudioResponse,
} from "../schemas/note/transcribe-audio";
import type { Note, NoteCreateBody, NoteUpdateBody, NoteListQuery } from "../schemas/note/crud";
import type { Task, TaskActionParams, TaskCreateBody, TaskListQuery, TaskListResponse } from "../schemas/task/crud";
import type {
  Query as PappersAutocompleteQuery,
  Response as PappersAutocompleteResponse,
} from "../schemas/pappers/autocomplete";
import type {
  Query as PappersEntrepriseQuery,
  Response as PappersEntrepriseResponse,
} from "../schemas/pappers/entreprise";
import type { PpeItem } from "../schemas/ppe-item";
import type { ProjectTypologyListParams, ProjectTypologyListResponse } from "../schemas/project-typology";
import type { Region, RegionListParams, RegionListResponse } from "../schemas/region";
import type { PpeRequestListParamsSchema, PpeRequestListResponseSchema } from "../schemas/ppe-request";
import type { PpeRequestEmail } from "../schemas/ppe-requests";
import type {
  ProfessionalCardCreationRequestBody,
  ProfessionalCardCreationRequestResponse,
  ProfessionalCardRequestListParamsSchema,
  ProfessionalCardRequestListResponseSchema,
  ProfessionalCardRequestSchema,
  ProfessionalCardRequestDetails,
  ProfessionalCardRequestRefusalBody,
  ProfessionalCardRequestUpdateBody,
  ProfessionalCardUpdateRequestBody,
  ProfessionalCardUpdateRequestResponse,
} from "../schemas/professional-card-request";
import type {
  QualificationCenterListParamsSchema,
  QualificationCenterListResponseSchema,
} from "../schemas/qualification-center";
import type {
  QualificationTrainingRequestListParams,
  QualificationTrainingRequestListResponse,
  QualificationTrainingRequest,
  CreateQualificationTrainingRequest,
  QualificationTrainingRequestUpdate,
  QualificationTrainingRequestDocumentsListParams,
  QualificationTrainingRequestDocumentResponse,
  QualificationTrainingRequestDocumentUpdateParams,
  QualificationTrainingRequestDocumentUpdate,
} from "../schemas/qualification-training-request";
import type {
  RemunerationTemplate,
  RemunerationTemplateInsertPayload,
  RemunerationTemplateListParams,
  RemunerationTemplateListResponse,
  RemunerationTemplateUpdatePayload,
} from "../schemas/remuneration/remuneration-template";
import type {
  ExtractResponse as NationalIdCardExtractResponse,
  WarnRequestBody as NationalIdCardWarnRequestBody,
  WarnResponse as NationalIdCardWarnResponse,
  ValidateRequestBody as NationalIdCardValidateRequestBody,
  ValidateResponse as NationalIdCardValidateResponse,
  StoreResponse as NationalIdCardStoreResponse,
} from "../schemas/document/document-types/national-id-card";
import type {
  Body as TalentDeepResearchDecomposeQueryBody,
  Response as TalentDeepResearchDecomposeQueryResponse,
} from "../schemas/talent/deep-research/decompose-query";
import type {
  Body as TalentDeepResearchExecuteQueryBody,
  Response as TalentDeepResearchExecuteQueryResponse,
} from "../schemas/talent/deep-research/execute-query";
import type { ListDuplicatesBody, ListDuplicatesResponse } from "../schemas/talent/list-duplicates";
import type { TalentSearchQuery, TalentSearchResponse } from "../schemas/talent/search";
import type { TalentCreateBody, TalentCreateResponse } from "../schemas/talent/talent-create";
import type { MergeTalentsBody, MergeTalentsResponse } from "../schemas/talent/merge";
import type { GetTalentSummaryQuery, TalentSummaryResponse } from "../schemas/talent/summary";
import type { TalentRecruitmentOfficePlacementResponse } from "../schemas/talent/recruitment-office-placement";
import type { TalentWorkAccidentSummaryResponse } from "../schemas/talent/work-accident-summary";
import type { TalentLoyaltyParams, TalentLoyaltyResponse } from "../schemas/talent/loyalty";
import type { LinkRecruitmentOfficePaymentDocumentResponseSchema } from "../schemas/recruitment-office-payment";
import type {
  RecruitmentOfficeInvoiceRequestListParamsSchema,
  RecruitmentOfficeInvoiceRequestListResponse,
} from "../schemas/recruitment-office-invoice-request";
import type {
  AppTalentAvailabilityBody,
  TalentAvailabilityListQuery,
  TalentAvailabilityListResponse,
  TalentAvailabilityResponse,
} from "../schemas/talent/availability";
import type {
  BlockAdvancePaymentBody,
  BlockedAdvancePaymentTalent,
  GenerateProfileSummaryBody,
  GenerateProfileSummaryResponse,
  GetTalentResumeParams,
  ReferencingTalentBody,
  ResumeFile,
  TalentAnonymousBioQueryParams,
  TalentAnonymousBioResponse,
  TalentBenchListResponse,
  TalentListParams,
  TalentListResponse,
  TalentProfessionalCardPhoto,
  TalentIdParams,
  TalentSubstituteBenchBody,
  TalentsTableQueryParams,
  UpdateTalentBody,
} from "../schemas/talent/talent";
import type { SourcingListQuery, TalentSourcingListResponse } from "../schemas/talent/sourcing";
import type {
  InterpretTalentSourcingPromptBody,
  InterpretTalentSourcingPromptResponse,
} from "../schemas/talent/sourcing-interpret-prompt";
import type {
  WorkAccidentLinkDocumentBodySchema,
  WorkAccidentListParamsSchema,
  WorkAccidentListResponseSchema,
  WorkAccidentDocumentsResponseSchema,
  WorkAccidentRemoveDocumentFileResponseSchema,
  WorkAccidentSchema,
  WorkAccidentUpdateStatusBodySchema,
} from "../schemas/work-accident";
import type {
  CreateTalentPushMarketingRequestBodySchema,
  CreateTalentPushMarketingRequestResponseSchema,
  DeactivateTalentPushMarketingRequestsResponseSchema,
} from "../schemas/talent-push-marketing-request";
import type {
  MedicalLeaveCreatePayload,
  MedicalLeaveListParams,
  MedicalLeaveListResponse,
  MedicalLeave,
  MedicalLeaveUpdatePayload,
} from "../schemas/medical-leave";
import type { WorkflowUpsertTalentBody, WorkflowUpsertTalentResponse } from "../schemas/workflow";
import type { WorkspaceTransferBodySchema, WorkspaceTransferCountsResponse } from "../schemas/workspace";
import type {
  AddressTableInsertBody,
  AddressTableInsertResponse,
  ContractSpecificityUpsertBody,
  ContractSpecificityUpsertResponse,
  ContractsTableQueryParams,
  ContractTableRow,
  PayTrackingCancelPayload,
  PayTrackingDetails,
  PayTrackingMetrics,
  PayTrackingsTableQueryParams,
  PayTrackingTableRow,
} from "../types";
import type { PldDocumentImportRequest, PldDocumentImportResponse } from "../types/document-import/import";
import type {
  CommercialConditionFilesResponse,
  GenerateCommercialConditionRequest,
} from "../types/commercial-condition";
import type {
  CommercialConditionFile,
  CommercialConditionListParams,
  CommercialConditionListResponse,
  CreateCommercialConditionRequest,
  CreateCommercialConditionResponse,
  DeleteCommercialConditionParams,
  DeleteCommercialConditionResponse,
  ValidateCommercialConditionResponse,
} from "../schemas/commercial-condition.schema";
import type {
  ContactInsertPayload,
  ContactTableRow,
  ContactUpdatePayload,
  GetContactAnswer,
  GetContactsSelectAnswer,
  GetContactDuplicatesAnswer,
  GetContactDuplicatesQueryParams,
  GetContactsSelectQueryParams,
  GetContactsTableQueryParams,
  PostContactAnswer,
  PutContactAnswer,
} from "../types/contact";
import type { AdditionalTimeResponse, AdditionalTimeUpsertBody } from "../types/contract-additional-time";
import type { ContractCommentTableQuery, ContractCommentTableRow } from "../types/contract-comment";
import type {
  Cooptation,
  CooptationCooptantSummaryQuery,
  CooptationCooptantSummaryResponse,
  CooptationCreateBody,
  CooptationDeleteResponse,
  CooptationIdParams,
  CooptationListQuery,
  CooptationListResponse,
  CooptationUpdateBody,
} from "../schemas/cooptation.schema";
import type {
  DocusealArchiveAnswer,
  DocusealArchivePayload,
  DocusealSendAnswer,
  DocusealSendPayload,
  DocusealTemplateAnswer,
  DocusealTemplatePayload,
} from "../types/docuseal";
import type {
  MedicalCheckupRequestApproveBody,
  MedicalCheckupRequestApproveResponse,
  MedicalCheckupRequestCreateBody,
  MedicalCheckupRequestCreateResponse,
  MedicalCheckupRequestUpdateBody,
  MedicalCheckupRequestUpdateResponse,
} from "../types/medical-checkup-request";
import type { CursorPagination } from "../types/pagination";
import type { PpeRequestApprovalBody } from "../types/ppe-request";
import type { QualificationFormQuestions } from "../types/qualification-form";
import type { TalentReviewStatus } from "../types/safety-book";
import type { TalentSuperSearchPayload, TalentSuperSearchResult, TalentTableRow } from "../types/talent";
import type {
  GetTeamMemberBusinessUnitForPayTrackingQueryParams,
  GetTeamMemberBusinessUnitForPayTrackingResponse,
  UpdateTeamMemberBusinessUnitForPayTrackingPayload,
} from "../types/team-member-business-unit-for-pay-tracking";
import type {
  ConsultantKpi,
  CreateTeamMemberDataBody,
  GetConsultantKpiQuery,
  ListTeamMemberDataQuery,
  ListTeamMemberDataResponse,
} from "../schemas/team-member-data";
import type {
  CompanyRemunerationSettings,
  CompanyRemunerationSettingsUpsertPayload,
  ReferenceDocumentIdsResponse,
} from "../schemas/remuneration/company-remuneration-settings";
import type { Contact, ContactsListParams, ContactsListResponse } from "../schemas/contact";
import type { AgencyListParams, AgencyListResponse } from "../schemas/agency";
import type { ReferenceCheckListParams, ReferenceCheckListResponse } from "../schemas/reference-check";
import type { CompanyInsuranceListParams, CompanyInsuranceListResponse } from "../schemas/company-insurance";
import type { MissionsQueryParamsSchema, MissionsResponseSchema } from "../schemas/mission/list";
import type { MissionCreateBody, MissionCreateResponse } from "../schemas/mission/mission-create";
import type { MissionTalentSourcingParams } from "../schemas/mission/talent-sourcing";
import type {
  findOrganizationParamsSchema,
  getOrganizationsQuerySchema,
  OrganizationSettingsResponse,
  organizationSchema,
} from "../schemas/organization.schema";
import type { GetOrganizationBrandingQuery, OrganizationBrandingResponse } from "../schemas/organization-branding";
import type {
  TalentComplianceQuery,
  TalentComplianceResponse,
  TalentsComplianceQuery,
  TalentsComplianceResponse,
} from "../schemas/talent/compliance";
import type { z } from "zod";

import type { IsReadyToVerifyQuery, IsReadyToVerifyResponse } from "../types/pay-tracking";
import type { AddressTableListQueryParams, AddressTableListResponse, BaseAddress } from "../schemas/address";
import type { DrivingRouteQuery, DrivingRouteResponse } from "../schemas/driving-route";
import type { BaseFile } from "../schemas/file";
import type { TalentRibSchema } from "../schemas/talent/rib";
import type { DepartmentListParams, DepartmentListResponse } from "../schemas/department";
import type {
  ApplicationBody,
  ApplicationListParams,
  ApplicationListResponse,
} from "../schemas/application/application";
import type { SecurityWithExpiry } from "../lib/filestack/client";
import type { BaseTalent } from "../utils/supabase.types";
import type { ContractDefinitiveEndRequestBody } from "../schemas/definitive-end";
import type { PostContractResponse } from "../schemas/contract/mutation";
import type {
  PostModificationAmendmentBody,
  PostModificationAmendmentResponse,
} from "../schemas/contract/modification-amendment";
import type { GetRemunerationProfileResponse } from "../schemas/remuneration/remuneration-profile";
import type {
  PatchContractRemunerationProfileBody,
  PatchContractRemunerationProfileResponse,
} from "../schemas/contract/patch-contract-remuneration-profile";
import type {
  PostExtensionAmendmentBody,
  PostExtensionAmendmentResponse,
} from "../schemas/contract/extension-amendment";
import type {
  PostContractWithAutoMissionBody,
  PostContractWithAutoMissionResponse,
} from "../schemas/contract/contract-with-auto-mission";
import type {
  TalentResumeDataResponse,
  UpdateTalentResumeDataBody,
  UpdateTalentResumeResponse,
} from "../schemas/talent/resume-data";
import type { TalentActivityListResponse } from "../schemas/talent/talent-activity";
import type {
  BusinessUnitListParams,
  BusinessUnitListResponse,
  BusinessUnitGetResponse,
  BusinessUnitIdParams,
  LinkTalentToMissionBusinessUnitBody,
  LinkTalentToMissionBusinessUnitParams,
  TalentBusinessUnit,
} from "../schemas/business-unit";
import type {
  CallLinkedNoteResponse,
  CallSchema,
  CallSummarySchema,
  CallSummarySettingsSchema,
  CallTranscriptionSchema,
  EndSimulatedSipsimCallBody,
  OngoingCall,
  OngoingCallTalent,
  PatchCallSummaryInstructionsPayload,
  SimulatedSipsimCall,
  SimulatedSipsimCallParams,
  StartSimulatedSipsimCallBody,
  UpdateCallPayload,
} from "../schemas/call.schema";
import type { ContractKeyTransmissionType } from "../schemas/contract/base";
import type {
  ThirdPartyNotice,
  ThirdPartyNoticeBody,
  ThirdPartyNoticeBulkImportBody,
  ThirdPartyNoticeBulkImportResponse,
  ThirdPartyNoticeClaimTypesResponse,
  ThirdPartyNoticeCreateBody,
  ThirdPartyNoticeDetail,
  ThirdPartyNoticeOrganismsResponse,
  ThirdPartyNoticeExtraction,
  ThirdPartyNoticeFileResponse,
  ThirdPartyNoticeListResponse,
  ThirdPartyNoticePayPeriodBody,
} from "../schemas/third-party-notice";

export type ApiEndpoint =
  | {
      path: "third-party-notices";
      method: "GET";
      query: {
        search?: string;
        pageIndex?: number;
        pageSize?: number;
        orderColumn?: string;
        orderDirection?: "asc" | "desc";
      };
      response: ThirdPartyNoticeListResponse;
    }
  | {
      path: "third-party-notices";
      method: "POST";
      body: ThirdPartyNoticeCreateBody;
      response: ThirdPartyNotice;
    }
  | {
      path: "third-party-notices/claim-types";
      method: "GET";
      response: ThirdPartyNoticeClaimTypesResponse;
    }
  | {
      path: "third-party-notices/files";
      method: "POST";
      body: FormData;
      response: ThirdPartyNoticeFileResponse;
    }
  | {
      path: "third-party-notices/bulk-import";
      method: "POST";
      body: ThirdPartyNoticeBulkImportBody;
      response: ThirdPartyNoticeBulkImportResponse;
    }
  | {
      path: "third-party-notices/extractions";
      method: "POST";
      body: { fileId: string };
      response: ThirdPartyNoticeExtraction;
    }
  | {
      path: "third-party-notices/:id";
      method: "GET";
      params: { id: string };
      response: ThirdPartyNoticeDetail;
    }
  | {
      path: "third-party-notices/:id/organisms";
      method: "GET";
      params: { id: string };
      response: ThirdPartyNoticeOrganismsResponse;
    }
  | {
      path: "third-party-notices/:id/talent";
      method: "POST";
      params: { id: string };
      body: { talentId: string };
      response: ThirdPartyNotice;
    }
  | {
      path: "third-party-notices/:id/refusal";
      method: "POST";
      params: { id: string };
      body: Record<string, never>;
      response: ThirdPartyNotice;
    }
  | {
      path: "third-party-notices/:id/closure";
      method: "POST";
      params: { id: string };
      body: Record<string, never>;
      response: ThirdPartyNotice;
    }
  | {
      path: "third-party-notices/:id/refusal-email";
      method: "POST";
      params: { id: string };
      body: Record<string, never>;
      response: ThirdPartyNotice;
    }
  | {
      path: "third-party-notices/:id/follow-up-email";
      method: "POST";
      params: { id: string };
      body: Record<string, never>;
      response: ThirdPartyNotice;
    }
  | {
      path: "third-party-notices/:id/seizure";
      method: "POST";
      params: { id: string };
      body: Record<string, never>;
      response: ThirdPartyNotice;
    }
  | {
      path: "third-party-notices/:id/installment";
      method: "POST";
      params: { id: string };
      body: Record<string, never>;
      response: ThirdPartyNotice;
    }
  | {
      path: "third-party-notices/:id/consultant-notification";
      method: "POST";
      params: { id: string };
      body: Record<string, never>;
      response: ThirdPartyNotice;
    }
  | {
      path: "third-party-notices/:id/talent-notification";
      method: "POST";
      params: { id: string };
      body: Record<string, never>;
      response: ThirdPartyNotice;
    }
  | {
      path: "third-party-notices/:id/pay-period";
      method: "PUT";
      params: { id: string };
      body: ThirdPartyNoticePayPeriodBody;
      response: ThirdPartyNotice;
    }
  | {
      path: "third-party-notices/:id";
      method: "PUT";
      params: { id: string };
      body: ThirdPartyNoticeBody;
      response: ThirdPartyNotice;
    }
  | {
      path: "job-matrix/tags";
      method: "GET";
      query: GetJobMatrixTagsQuery;
      response: GetJobMatrixTagsAnswer;
    }
  | {
      path: "job-matrix/training/classifiable-talents";
      method: "GET";
      query: GetClassifiableTalentsQuery;
      response: GetClassifiableTalentsAnswer;
    }
  | {
      path: "job-matrix/training/classifiable-talent-counts";
      method: "GET";
      response: GetClassifiableTalentCountsAnswer;
    }
  | {
      path: "job-matrix/training/classified-talents";
      method: "GET";
      query: GetClassifiedTalentsQuery;
      response: GetClassifiedTalentsAnswer;
    }
  | {
      path: "job-matrix/training/talents/:talentId/tags";
      method: "GET";
      params: { talentId: string };
      response: GetTalentClassificationTagsAnswer;
    }
  | {
      path: "job-matrix/training/talents/:talentId/tags";
      method: "PUT";
      params: { talentId: string };
      body: ValidateTalentReferenceClassificationPayload;
      response: ValidateTalentReferenceClassificationAnswer;
    }
  | {
      path: "job-matrix/training/talents/:talentId/business-unit";
      method: "PATCH";
      params: { talentId: string };
      body: UpdateTalentJobMatrixTrainClassificationBusinessUnitPayload;
      response: UpdateTalentJobMatrixTrainClassificationBusinessUnitResponse;
    }
  | {
      path: "pld/qualifications";
      method: "GET";
      query: PldQualificationsListQuery;
      response: PldQualificationsListResponse;
    }
  | {
      path: "pld/commercials";
      method: "GET";
      query: PldCommercialsListQuery;
      response: PldCommercialsListResponse;
    }
  | {
      path: "pld/consignes";
      method: "GET";
      query: PldConsignesListQuery;
      response: PldConsignesListResponse;
    }
  | {
      path: "pld/primes";
      method: "GET";
      query: PldPrimesListQuery;
      response: PldPrimesListResponse;
    }
  | {
      path: "pld/contract/:contractId/interimaire/preview";
      method: "GET";
      params: ContractParams;
      response: PldInterimairePreviewResponse;
    }
  | {
      path: "pld/contract/:contractId/interimaire";
      method: "POST";
      params: ContractParams;
      body: PldInterimaireCreateBody;
      response: PldInterimaireCreateResponse;
    }
  | {
      path: "pld/contract/:contractId/interimaire";
      method: "PATCH";
      params: ContractParams;
      body: PldInterimaireUpdateBody;
      response: PldInterimaireUpdateResponse;
    }
  | {
      path: "pld/contract/:contractId/client/preview";
      method: "GET";
      params: ContractParams;
      response: PldClientPreviewResponse;
    }
  | {
      path: "pld/contract/:contractId/client";
      method: "POST";
      params: ContractParams;
      body: PldClientCreateBody;
      response: PldClientCreateResponse;
    }
  | {
      path: "pld/contract/:contractId/documents/preview";
      method: "GET";
      params: ContractParams;
      response: PldDocumentsPreviewResponse;
    }
  | {
      path: "pld/contract/:contractId/documents";
      method: "POST";
      params: ContractParams;
      body: PldDocumentsUploadBody;
      response: PldDocumentsUploadResponse;
    }
  | {
      path: "pld/contract/:contractId/contracts/amendment";
      method: "GET";
      params: ContractParams;
      response: PldContractsListWithLatestResponse;
    }
  | {
      path: "pld/contract/:contractId/amendment/extension";
      method: "POST";
      params: ContractParams;
      body: PldContractExtensionCreateBody;
      response: PldContractExtensionCreateResponse;
    }
  | {
      path: "pld/contract-extension/batch-preview";
      method: "GET";
      query: ContractsTableQueryParams;
      response: PldExtensionAmendmentBatchPreviewResponseSchema;
    }
  | {
      path: "pld/contract-extension/batch-send";
      method: "POST";
      query: ContractsTableQueryParams;
      body: PldExtensionAmendmentBatchSendBodySchema;
      response: PldJobTriggerResponseSchema;
    }
  | {
      path: "pld/contract/:contractId/pld-contract/preview";
      method: "GET";
      params: ContractParams;
      query: PldContractPreviewQuery;
      response: PldContractPreviewResponse;
    }
  | {
      path: "pld/contract/:contractId/pld-contract";
      method: "POST";
      params: ContractParams;
      body: PldContractCreateBody;
      response: PldContractCreateResponse;
    }
  | {
      path: "documents/national-id-card/extract";
      method: "POST";
      body: FormData;
      response: NationalIdCardExtractResponse;
    }
  | {
      path: "documents/national-id-card/warn";
      method: "POST";
      body: NationalIdCardWarnRequestBody;
      response: NationalIdCardWarnResponse;
    }
  | {
      path: "documents/national-id-card/verify";
      method: "POST";
      body: NationalIdCardValidateRequestBody;
      response: NationalIdCardValidateResponse;
    }
  | {
      path: "documents/national-id-card/store";
      method: "POST";
      body: FormData;
      response: NationalIdCardStoreResponse;
    }
  | {
      path: "talent/:talentId/safety-book/review/status";
      method: "GET";
      params: { talentId: string };
      response: TalentReviewStatus;
    }
  | {
      path: "talent/:talentId/global-compliance";
      method: "GET";
      params: { talentId: string };
      response: TalentComplianceResponse;
      query: TalentComplianceQuery;
    }
  | {
      path: "talent/:id/professional-card-picture";
      method: "POST";
      params: { id: string };
      response: BaseFile;
    }
  | {
      path: "talent/:id/professional-card-photo";
      method: "GET";
      params: { id: string };
      response: TalentProfessionalCardPhoto;
    }
  | {
      path: "talent/:id/professional-card-photo";
      method: "POST";
      params: { id: string };
      body: FormData;
      response: BaseFile;
    }
  | {
      path: "talents/global-compliance";
      method: "GET";
      query: TalentsComplianceQuery;
      response: TalentsComplianceResponse;
    }
  | {
      path: "ppe-request/:id/approval";
      method: "PUT";
      body: PpeRequestApprovalBody;
      response: { success: boolean };
      params: { id: string };
    }
  | {
      path: "timesheet/get-timesheets";
      method: "GET";
      query: ListTimesheetsQuery;
      response: TimesheetsResponse;
    }
  | {
      path: "timesheet/reset-signatures";
      method: "POST";
      body: TimesheetResetBody;
      response: TimesheetResetResponse;
    }
  | {
      path: "job-posting";
      method: "GET";
      query: JobPostingQuery;
      response: GetJobPostingResponse;
    }
  | {
      path: "job-posting";
      method: "POST";
      query: JobPostingQuery;
      response: CreateJobOfferResponse;
    }
  | {
      path: "job-posting";
      method: "PUT";
      query: JobPostingQuery;
      response: UpdateJobOfferResponse;
    }
  | {
      path: "job-posting";
      method: "DELETE";
      query: JobPostingQuery;
      response: DeleteJobOfferResponse;
    }
  | {
      path: "team-member/absence";
      method: "POST";
      body: TeamMemberAbsenceBody;
      response: TeamMemberAbsenceResponse;
    }
  | {
      path: "team-member";
      method: "GET";
      query: ListTeamMembersQuery;
      response: ListTeamMembersResponse;
    }
  | {
      path: "team-member/organigram";
      method: "GET";
      query: TeamMemberOrganigramListParams;
      response: ListTeamMemberOrganigramResponse;
    }
  | {
      path: "team-member/:id";
      method: "GET";
      params: { id: string };
      response: TeamMemberProfileResponse;
    }
  | {
      path: "team-member/revert-absence";
      method: "POST";
      body: TeamMemberRevertAbsenceBody;
      response: TeamMemberAbsenceResponse;
    }
  | {
      path: "team-member/offboarding";
      method: "GET";
      query: TeamMemberOffboardingStatusQuery;
      response: TeamMemberOffboardingStatusResponse;
    }
  | {
      path: "team-member/offboarding";
      method: "PUT";
      body: TeamMemberOffboardingBody;
      response: TeamMemberOffboardingStatusResponse;
    }
  | {
      path: "team-member/invite";
      method: "POST";
      body: InviteTeamMemberBody;
      response: InviteTeamMemberResponse;
    }
  | {
      path: "team-member-data";
      method: "GET";
      query: ListTeamMemberDataQuery;
      response: ListTeamMemberDataResponse;
    }
  | {
      path: "team-member-data/consultant-kpi";
      method: "GET";
      query: GetConsultantKpiQuery;
      response: ConsultantKpi;
    }
  | {
      path: "team-member-data";
      method: "POST";
      body: CreateTeamMemberDataBody;
      response: ListTeamMemberDataResponse;
    }
  | {
      path: "qualification_training_request/:id/approve";
      method: "PUT";
      body: QualificationTrainingRequestApproveQuery;
      response: QualificationTrainingRequestApproveResponse;
      params: { id: string };
    }
  | {
      path: "navbar-count";
      method: "GET";
      response: NavButtonCount;
    }
  | {
      path: "action-center/counts";
      method: "GET";
      response: ActionCenterCounts;
    }
  | {
      path: "document/send/contact";
      method: "POST";
      body: DocumentSenderContactBody;
      response: { success: boolean };
    }
  | {
      path: "document/send/talent";
      method: "POST";
      body: DocumentSenderTalentBody;
      response: { success: boolean };
    }
  | {
      path: "email/send_html";
      method: "POST";
      body: HtmlEmailSenderBody;
      response: { success: boolean };
    }
  | {
      path: "contract/notify-talent-contract-to-sign";
      method: "POST";
      body: TalentContractToSignNotificationBody;
      response: { success: boolean };
    }
  | {
      path: "talent/super-search";
      method: "POST";
      body: TalentSuperSearchPayload;
      response: TalentSuperSearchResult;
    }
  | {
      path: "mission/:missionId/followers";
      method: "GET";
      params: GetMissionFollowersParams;
      response: GetMissionFollowersResponse;
    }
  | {
      path: "mission/:missionId/followers";
      method: "POST";
      params: FollowMissionParams;
      body: FollowMissionBody;
      response: FollowMissionResponse;
    }
  | {
      path: "mission/:missionId/followers/:teamMemberId";
      method: "DELETE";
      params: UnfollowMissionParams;
      response: UnfollowMissionResponse;
    }
  | {
      path: "sms";
      method: "POST";
      body: SmsSenderBody;
      response: SmsSendResponse;
    }
  | {
      path: "sms/:id";
      method: "PATCH";
      params: SmsIdParams;
      body: UpdateSmsContentBody;
      response: SmsOkResponse;
    }
  | {
      path: "sms/:id";
      method: "DELETE";
      params: SmsIdParams;
      response: SmsOkResponse;
    }
  | {
      path: "sms/reminders";
      method: "GET";
      query: SmsReminderListParams;
      response: SmsReminderListResponse;
    }
  | {
      path: "sms/reminders";
      method: "POST";
      body: CreateSmsRemindersBody;
      response: SmsOkResponse;
    }
  | {
      path: "workflows/upsert-talent";
      method: "POST";
      body: WorkflowUpsertTalentBody;
      response: WorkflowUpsertTalentResponse;
    }
  | {
      path: "talent/list-duplicates";
      method: "POST";
      body: ListDuplicatesBody;
      response: ListDuplicatesResponse;
    }
  | {
      path: "medical-checkup-request/send-medispace-email";
      method: "POST";
      body: MedispaceEmailSenderBody;
      response: { success: boolean };
    }
  | {
      path: "medical-checkup-request/:id/approve";
      method: "PUT";
      body: MedicalCheckupRequestApproveBody;
      response: MedicalCheckupRequestApproveResponse;
      params: { id: string };
    }
  | {
      path: "medical-checkup-request";
      method: "POST";
      body: MedicalCheckupRequestCreateBody;
      response: MedicalCheckupRequestCreateResponse;
    }
  | {
      path: "professional-card-request";
      method: "POST";
      body: ProfessionalCardCreationRequestBody;
      response: ProfessionalCardCreationRequestResponse;
    }
  | {
      path: "professional-card-request/card-update";
      method: "POST";
      body: ProfessionalCardUpdateRequestBody;
      response: ProfessionalCardUpdateRequestResponse;
    }
  | {
      path: "professional-card-request/:id";
      method: "GET";
      params: { id: string };
      response: ProfessionalCardRequestDetails;
    }
  | {
      path: "professional-card-request/:id";
      method: "PATCH";
      params: { id: string };
      body: ProfessionalCardRequestUpdateBody;
      response: ProfessionalCardRequestSchema;
    }
  | {
      path: "professional-card-request/:id/refusal";
      method: "POST";
      params: { id: string };
      body: ProfessionalCardRequestRefusalBody;
      response: ProfessionalCardRequestSchema;
    }
  | {
      path: "medical-checkup-request/:id";
      method: "PATCH";
      body: MedicalCheckupRequestUpdateBody;
      response: MedicalCheckupRequestUpdateResponse;
      params: { id: string };
    }
  | {
      path: "analytics/talent/identify";
      method: "POST";
      body: AnalyticsTalentIdentifyBody;
      response: AnalyticsTalentIdentifyResponse;
    }
  | {
      path: "workspace/transfer";
      method: "POST";
      body: WorkspaceTransferBodySchema;
      response: { success: boolean };
    }
  | {
      path: "workspaces/:id/transfer-counts";
      method: "GET";
      params: { id: string };
      response: WorkspaceTransferCountsResponse;
    }
  | {
      path: "job/autocomplete";
      method: "GET";
      query: JobAutocompleteQuery;
      response: JobAutocompleteResponse;
    }
  | {
      path: "job/alternatives";
      method: "GET";
      query: JobAlternativesQuery;
      response: JobAlternativesResponse;
    }
  | {
      path: "public/get-timesheets";
      method: "GET";
      query: ListTimesheetsFromContactQuery;
      headers: {
        "Content-Type": "application/json";
        Authorization: `Bearer ${string}`;
      };
      response: CustomerTimesheetsResponse;
    }
  | {
      path: "public/qualification-form";
      method: "GET";
      query: { token: string };
      headers: {
        "Content-Type": "application/json";
        Authorization: `Bearer ${string}`;
      };
      response: QualificationFormQuestions;
    }
  | {
      path: "contract-coefficients/list";
      method: "POST";
      body: ContractCoefficientsListParams;
      response: ContractCoefficientsResult;
    }
  | {
      path: "talent/:id/availability";
      method: "GET";
      response: TalentAvailabilityResponse;
      params: { id: string };
    }
  | {
      path: "talent/:id/recruitment-office-placement";
      method: "GET";
      response: TalentRecruitmentOfficePlacementResponse;
      params: { id: string };
    }
  | {
      path: "talent/:id/work-accident-summary";
      method: "GET";
      response: TalentWorkAccidentSummaryResponse;
      params: { id: string };
    }
  | {
      path: "talent/:id/loyalty";
      method: "GET";
      params: TalentLoyaltyParams;
      response: TalentLoyaltyResponse;
    }
  | {
      path: "recruitment-office-invoice-requests";
      method: "GET";
      query: RecruitmentOfficeInvoiceRequestListParamsSchema;
      response: RecruitmentOfficeInvoiceRequestListResponse;
    }
  | {
      path: "talent/:id/advance-payment-eligibility";
      method: "GET";
      params: TalentIdParams;
      response: AdvancePaymentEligibilityResponse;
    }
  | {
      path: "recruitment-office-payments/:paymentId/documents";
      method: "POST";
      body: { documentId: string };
      params: { paymentId: string };
      response: LinkRecruitmentOfficePaymentDocumentResponseSchema;
    }
  | {
      path: "talent/:id/availability";
      method: "POST";
      body: AppTalentAvailabilityBody;
      response: { success: boolean };
      params: { id: string };
    }
  | {
      path: "talents/availability";
      method: "GET";
      query: TalentAvailabilityListQuery;
      response: TalentAvailabilityListResponse;
    }
  | {
      path: "talents/merge";
      method: "POST";
      body: MergeTalentsBody;
      response: MergeTalentsResponse;
    }
  | {
      path: "talent/:id/summary";
      method: "GET";
      query: GetTalentSummaryQuery;
      response: TalentSummaryResponse;
      params: { id: string };
    }
  | {
      path: "agencies";
      method: "GET";
      query: AgencyListParams;
      response: AgencyListResponse;
    }
  | {
      path: "company/autocomplete";
      method: "GET";
      query: CompanyAutocompleteQuery;
      response: CompanyAutocompleteResponse;
    }
  | {
      path: "company-suggestions";
      method: "GET";
      query: CompanySuggestionListQuery;
      response: CompanySuggestionListResponse;
    }
  | {
      path: "companies";
      method: "GET";
      query: CompanyListParams;
      response: CompanyListResponse;
    }
  | {
      path: "companies/:id";
      method: "GET";
      params: { id: string };
      response: Company;
    }
  | {
      path: "companies/:id";
      method: "POST";
      body: CompanyInsertPayload;
      response: Company;
    }
  | {
      path: "companies/:id";
      method: "PATCH";
      params: { id: string };
      body: CompanyUpdatePayload;
      response: Company;
    }
  | {
      path: "companies/:id/risk";
      method: "PATCH";
      params: { id: string };
      body: CompanyRiskUpdatePayload;
      response: Company;
    }
  | {
      path: "companies/:id/pappers-sync";
      method: "POST";
      params: { id: string };
      response: Company;
    }
  | {
      path: "credit-insurance/access";
      method: "GET";
      response: CreditInsuranceAccessResponse;
    }
  | {
      path: "companies/:companyId/credit-insurer-requests";
      method: "GET";
      params: { companyId: string };
      query: CreditInsurerRequestListParams;
      response: CreditInsurerRequestListResponse;
    }
  | {
      path: "companies/:companyId/credit-insurer-covers";
      method: "GET";
      params: { companyId: string };
      response: CoverListResponse;
    }
  | {
      path: "companies/:companyId/credit-insurer-covers/sync";
      method: "POST";
      params: { companyId: string };
      response: CoverListResponse;
    }
  | {
      path: "credit-insurer-request/:id/check";
      method: "POST";
      params: { id: string };
      response: CreditInsurerRequestResponse;
    }
  | {
      path: "companies/:companyId/credit-insurer-request";
      method: "POST";
      params: { companyId: string };
      body: RequestCoverPayload;
      response: CreditInsurerRequestResponse;
    }
  | {
      path: "geo/communes";
      method: "GET";
      query: GeoCommunesQuery;
      response: GeoCommunesResponse;
    }
  | {
      path: "pappers/autocomplete";
      method: "GET";
      query: PappersAutocompleteQuery;
      response: PappersAutocompleteResponse;
    }
  | {
      path: "pappers/entreprise";
      method: "GET";
      query: PappersEntrepriseQuery;
      response: PappersEntrepriseResponse;
    }
  | {
      path: "feature-flag/posthog";
      method: "GET";
      query: FeatureFlagQuery;
      response: FeatureFlagResponse;
    }
  | {
      path: "feature-flags";
      method: "GET";
      response: FeatureFlagsResponse;
    }
  | {
      path: "company-insurances";
      method: "GET";
      query: CompanyInsuranceListParams;
      response: CompanyInsuranceListResponse;
    }
  | {
      path: "contracts";
      method: "GET";
      query: ContractsQueryParams;
      response: ContractsResponse;
    }
  | {
      path: "missions";
      method: "GET";
      query: MissionsQueryParamsSchema;
      response: MissionsResponseSchema;
    }
  | {
      path: "missions";
      method: "POST";
      body: MissionCreateBody;
      response: MissionCreateResponse;
    }
  | {
      path: "missions/:missionId/talent-sourcing-params";
      method: "GET";
      params: { missionId: string };
      response: MissionTalentSourcingParams;
    }
  | {
      path: "contracts/by-talent/:talentId";
      method: "GET";
      params: ContractsByTalentParams;
      response: ContractsByTalentResponse;
    }
  | {
      path: "contracts/by-talent/:talentId/contract-keys";
      method: "GET";
      params: ContractsByTalentParams;
      response: TalentContractKeysResponse;
    }
  | {
      path: "contracts/by-talent/:talentId/summary";
      method: "GET";
      params: ContractsByTalentParams;
      response: TalentContractSummary;
    }
  | {
      path: "contracts/table";
      method: "GET";
      query: ContractsTableQueryParams & CursorPagination;
      response: ContractTableRow[];
    }
  | {
      path: "docuseal/generate-template";
      method: "POST";
      body: DocusealTemplatePayload;
      response: DocusealTemplateAnswer;
    }
  | {
      path: "docuseal/send";
      method: "POST";
      body: DocusealSendPayload;
      response: DocusealSendAnswer;
    }
  | {
      path: "docuseal/archive";
      method: "POST";
      body: DocusealArchivePayload;
      response: DocusealArchiveAnswer;
    }
  | {
      path: "contract-specificity/upsert";
      method: "POST";
      body: ContractSpecificityUpsertBody;
      response: ContractSpecificityUpsertResponse;
    }
  | {
      path: "pay-trackings/table";
      method: "GET";
      query: PayTrackingsTableQueryParams;
      response: PayTrackingTableRow[];
    }
  | {
      path: "pay-tracking/:id";
      method: "GET";
      params: { id: string };
      response: PayTrackingDetails;
    }
  | {
      path: "pay-tracking/:id/cancel";
      method: "PUT";
      body: PayTrackingCancelPayload;
      response: { success: boolean };
      params: { id: string };
    }
  | {
      path: "pay-tracking/:id/reactivate";
      method: "PUT";
      response: { success: boolean; id: string };
      params: { id: string };
    }
  | {
      path: "pay-tracking/:id/documents";
      method: "POST";
      body: AddPayTrackingDocumentBody;
      response: { success: boolean; id: string };
      params: { id: string };
    }
  | {
      path: "timesheet/send-to-client";
      method: "POST";
      body: TimesheetSendToClientBody;
      response: TimesheetSendToClientResponse;
    }
  | {
      path: "talents";
      method: "POST";
      body: TalentCreateBody;
      response: TalentCreateResponse;
    }
  | {
      path: "talents/table";
      method: "GET";
      query: TalentsTableQueryParams & CursorPagination;
      response: TalentTableRow[];
    }
  | {
      path: "talents/search";
      method: "GET";
      query: TalentSearchQuery;
      response: TalentSearchResponse;
    }
  | {
      path: "global-search";
      method: "GET";
      query: GlobalSearchQuery;
      response: GlobalSearchResponse;
    }
  | {
      path: "resume/parse-contact-details";
      method: "POST";
      body: FormData;
      response: ParseResumeContactDetailsResponse;
    }
  | {
      path: "talent/:talentId/resume";
      method: "POST";
      params: CreateTalentResumeParams;
      body: FormData;
      response: CreateTalentResumeResponse;
    }
  | {
      path: "talent/:talentId/resume/:resumeId";
      method: "DELETE";
      params: DeleteTalentResumeParams;
      body: FormData;
      response: DeleteTalentResumeResponse;
    }
  | {
      path: "file/orphans";
      method: "GET";
      params: undefined;
      response: FileOrphanResponseType;
    }
  | {
      path: "talent/:talentId/generate-profile-summary";
      method: "POST";
      body: GenerateProfileSummaryBody;
      response: GenerateProfileSummaryResponse;
      params: { talentId: string };
    }
  | {
      path: "ppe-request/send-emails";
      method: "POST";
      body: PpeRequestEmail;
      response: { success: boolean };
    }
  | {
      path: "talent/:talentId/generate-talent-bio";
      method: "GET";
      params: TalentAnonymousBioQueryParams;
      response: TalentAnonymousBioResponse;
    }
  | {
      path: "analytics/talent/track-event";
      method: "POST";
      body: AnalyticsTalentTrackEventBody;
      response: AnalyticsTalentTrackEventResponse;
    }
  | {
      path: "analytics/talent/track-page-view";
      method: "POST";
      body: AnalyticsTalentTrackPageViewBody;
      response: AnalyticsTalentTrackPageViewResponse;
    }
  | {
      path: "analytics/team-member/identify";
      method: "POST";
      body: AnalyticsTeamMemberIdentifyBody;
      response: AnalyticsTeamMemberIdentifyResponse;
    }
  | {
      path: "analytics/team-member/track-event";
      method: "POST";
      body: AnalyticsTeamMemberTrackEventBody;
      response: AnalyticsTeamMemberTrackEventResponse;
    }
  | {
      path: "analytics/team-member/track-page-view";
      method: "POST";
      body: AnalyticsTeamMemberTrackPageViewBody;
      response: AnalyticsTeamMemberTrackPageViewResponse;
    }
  | {
      path: "customerio/talent/sync";
      method: "POST";
      body: CustomerioTalentSyncBody;
      response: CustomerioTalentSyncResponse;
    }
  | {
      path: "customerio/mission/sync";
      method: "POST";
      body: CustomerioMissionSyncBody;
      response: CustomerioMissionSyncResponse;
    }
  | {
      path: "customerio/application/sync";
      method: "POST";
      body: CustomerioApplicationSyncBody;
      response: CustomerioApplicationSyncResponse;
    }
  | {
      path: "customerio/contract/sync";
      method: "POST";
      body: CustomerioContractSyncBody;
      response: CustomerioContractSyncResponse;
    }
  | {
      path: "hiring-pools/table";
      method: "GET";
      query: HiringPoolTableQueryParams & CursorPagination;
      response: HiringPool[];
    }
  | {
      path: "hiring-pools";
      method: "GET";
      query: HiringPoolListQueryParams & CursorPagination;
      response: { data: HiringPoolListResponse[]; totalCount: number };
    }
  | {
      path: "hiring-pools/search";
      method: "GET";
      query: HiringPoolSearchQuery;
      response: HiringPool[];
    }
  | {
      path: "hiring-pools/list";
      method: "GET";
      query: HiringPoolPageListParams;
      response: HiringPoolPageListResponse;
    }
  | {
      path: "hiring-pools/:id";
      method: "GET";
      params: { id: string };
      response: HiringPool;
    }
  | {
      path: "hiring-pools/:id";
      method: "PUT";
      body: UpdateHiringPoolBody;
      params: { id: string };
      response: HiringPool;
    }
  | {
      path: "hiring-pools";
      method: "POST";
      body: CreateHiringPoolBody;
      response: HiringPool;
    }
  | {
      path: "hiring-pools/:id";
      method: "DELETE";
      params: { id: string };
      response: DeleteHiringPoolResponse;
    }
  | {
      path: "reference-checks";
      method: "GET";
      query: ReferenceCheckListParams;
      response: ReferenceCheckListResponse;
    }
  | {
      path: "signature-requests";
      method: "POST";
      body: FormData;
      response: SignatureRequestResponse;
    }
  | {
      path: "signature-requests";
      method: "GET";
      query: SignatureRequestListParams;
      response: SignatureRequestListResponse;
    }
  | {
      path: "signature-requests/signing-options";
      method: "GET";
      query: SigningOptionsParams;
      response: SigningOptionsResponse;
    }
  | {
      path: "signature-requests/:id/cancellation";
      method: "POST";
      params: { id: string };
      response: SignatureRequestResponse;
    }
  | {
      path: "wanted-profiles";
      method: "GET";
      query: WantedProfileListParams;
      response: WantedProfileListResponse;
    }
  | {
      path: "wanted-profiles/job-codes";
      method: "GET";
      query: WantedProfileJobCodesParams;
      response: WantedProfileJobCodesResponse;
    }
  | {
      path: "wanted-profiles";
      method: "POST";
      body: CreateWantedProfileBody;
      response: WantedProfile;
    }
  | {
      path: "wanted-profiles/:id";
      method: "PATCH";
      params: WantedProfileIdParams;
      body: UpdateWantedProfileBody;
      response: WantedProfile;
    }
  | {
      path: "saved-talent-searches";
      method: "GET";
      query: ListSavedTalentSearchesQuery;
      response: ListSavedTalentSearchesResponse;
    }
  | {
      path: "saved-talent-searches";
      method: "POST";
      body: CreateSavedTalentSearchBody;
      response: SavedTalentSearch;
    }
  | {
      path: "saved-talent-searches/:id";
      method: "GET";
      params: { id: string };
      response: SavedTalentSearch;
    }
  | {
      path: "saved-talent-searches/:id";
      method: "PATCH";
      body: UpdateSavedTalentSearchBody;
      params: { id: string };
      response: SavedTalentSearch;
    }
  | {
      path: "saved-talent-searches/:id";
      method: "DELETE";
      params: { id: string };
      response: DeleteSavedTalentSearchResponse;
    }
  | {
      path: "saved-talent-searches/:id/consultation";
      method: "POST";
      params: { id: string };
      body: RecordSavedTalentSearchConsultationBody;
      response: RecordSavedTalentSearchConsultationResponse;
    }
  | {
      path: "saved-talent-searches/email-alert-preference";
      method: "PUT";
      body: SavedTalentSearchEmailAlertPreference;
      response: SavedTalentSearchEmailAlertPreferenceResponse;
    }
  | {
      path: "mission/table";
      method: "GET";
      query: MissionTableQueryParams & CursorPagination;
      response: MissionTableRow[];
    }
  | {
      path: "contact-agency";
      method: "POST";
      body: { contactId: string; agencyId: string };
      response: { success: boolean };
    }
  | {
      path: "team-member/:id/autonomy";
      method: "PUT";
      params: { id: string };
      body: TeamMemberAutonomyBody;
      response: { success: boolean };
    }
  | {
      path: "address-table/insert";
      method: "POST";
      body: AddressTableInsertBody;
      response: AddressTableInsertResponse;
    }
  | {
      path: "additional-time/upsert";
      method: "POST";
      body: AdditionalTimeUpsertBody;
      response: AdditionalTimeResponse;
    }
  | {
      path: "contacts";
      method: "GET";
      query: ContactsListParams;
      response: ContactsListResponse;
    }
  | {
      path: "contact/table";
      method: "GET";
      query: CursorPagination & GetContactsTableQueryParams;
      response: ContactTableRow[];
    }
  | {
      path: "contact/:id/archive";
      method: "PUT";
      params: { id: string };
      response: Contact;
    }
  | {
      path: "contact/select";
      method: "GET";
      query: CursorPagination & GetContactsSelectQueryParams;
      response: GetContactsSelectAnswer;
    }
  | {
      path: "contact/duplicates";
      method: "GET";
      query: GetContactDuplicatesQueryParams;
      response: GetContactDuplicatesAnswer;
    }
  | {
      path: "contact";
      method: "POST";
      body: ContactInsertPayload;
      response: PostContactAnswer;
    }
  | {
      path: "contact/:id";
      method: "PUT";
      params: { id: string };
      body: ContactUpdatePayload;
      response: PutContactAnswer;
    }
  | {
      path: "contact/:id";
      method: "GET";
      params: { id: string };
      response: GetContactAnswer;
    }
  | {
      path: "contract/:contractId/cancel";
      method: "POST";
      params: { contractId: string };
      response: { success: boolean };
    }
  | {
      path: "contract/:contractId/pld/preview-interimaire";
      method: "GET";
      params: { contractId: string };
      response: PldInterimairePreviewResponse;
    }
  | {
      path: "cooptations";
      method: "GET";
      query: CooptationListQuery;
      response: CooptationListResponse;
    }
  | {
      path: "cooptations/cooptant-summary";
      method: "GET";
      query: CooptationCooptantSummaryQuery;
      response: CooptationCooptantSummaryResponse;
    }
  | {
      path: "cooptations";
      method: "POST";
      body: CooptationCreateBody;
      response: Cooptation;
    }
  | {
      path: "cooptations/:id";
      method: "PATCH";
      params: CooptationIdParams;
      body: CooptationUpdateBody;
      response: Cooptation;
    }
  | {
      path: "cooptations/:id";
      method: "DELETE";
      params: CooptationIdParams;
      response: CooptationDeleteResponse;
    }
  | {
      path: "contract-comment/:contractId";
      method: "GET";
      params: ContractCommentTableQuery;
      response: ContractCommentTableRow[];
    }
  | {
      path: "document";
      method: "GET";
      query: { talent_id: string };
      response: FullDocument[];
    }
  | {
      path: "document/:id";
      method: "GET";
      params: { id: string };
      query: { includeReplaced?: boolean };
      response: FullDocument;
    }
  | {
      path: "document/:id";
      method: "DELETE";
      params: { id: string };
      response: { success: boolean };
    }
  | {
      path: "document/:id/download";
      method: "GET";
      params: { id: string };
      response: Response;
    }
  | {
      path: "document/:id/social-security-number";
      method: "GET";
      params: { id: string };
      response: GetSocialSecurityCardNumberResponse;
    }
  | {
      path: "document/:id/internal-review/reject";
      method: "POST";
      params: { id: string };
      body: InternalReviewRejectionBody;
      response: InternalReviewRejectionResponse;
    }
  | {
      path: "filestack/security";
      method: "GET";
      response: SecurityWithExpiry;
    }
  | {
      path: "file/convert/docx-to-pdf";
      method: "POST";
      body: FormData;
      response: Response;
    }
  | {
      path: "commercial-conditions";
      method: "GET";
      query: CommercialConditionListParams;
      response: CommercialConditionListResponse;
    }
  | {
      path: "commercial-conditions/:id";
      method: "DELETE";
      params: DeleteCommercialConditionParams;
      response: DeleteCommercialConditionResponse;
    }
  | {
      path: "commercial-condition";
      method: "POST";
      body: CreateCommercialConditionRequest;
      response: CreateCommercialConditionResponse;
    }
  | {
      path: "commercial-condition/:id/validate";
      method: "POST";
      params: { id: string };
      response: ValidateCommercialConditionResponse;
    }
  | {
      path: "commercial-condition/:id/files";
      method: "GET";
      params: { id: string };
      response: CommercialConditionFilesResponse;
    }
  | {
      path: "commercial-condition/:id/generate";
      method: "POST";
      params: { id: string };
      body: GenerateCommercialConditionRequest;
      response: CommercialConditionFile;
    }
  | {
      path: "commercial-condition/:id/signed-file";
      method: "POST";
      params: { id: string };
      body: FormData;
      response: CommercialConditionFile;
    }
  | {
      path: "pay-tracking/metrics";
      method: "GET";
      query: PayTrackingsTableQueryParams;
      response: PayTrackingMetrics;
    }
  | {
      path: "note/transcribe-audio";
      method: "POST";
      body: NoteTranscribeAudioBody;
      response: NoteTranscribeAudioResponse;
    }
  | {
      path: "note/summarize-content";
      method: "POST";
      body: NoteSummarizeContentBody;
      response: NoteSummarizeContentResponse;
    }
  | {
      path: "note/generate-title";
      method: "POST";
      body: NoteGenerateTitleBody;
      response: NoteGenerateTitleResponse;
    }
  | {
      path: "notes/call/:callId";
      method: "GET";
      params: { callId: string };
      response: CallLinkedNoteResponse;
    }
  | {
      path: "notes";
      method: "GET";
      query: NoteListQuery;
      response: Note[];
    }
  | {
      path: "notes";
      method: "POST";
      body: NoteCreateBody;
      response: Note;
    }
  | {
      path: "notes/:noteId";
      method: "GET";
      params: { noteId: string };
      response: Note;
    }
  | {
      path: "notes/:noteId";
      method: "PATCH";
      params: { noteId: string };
      body: NoteUpdateBody;
      response: Note;
    }
  | {
      path: "notes/:noteId";
      method: "DELETE";
      params: { noteId: string };
      response: {
        success: boolean;
      };
    }
  | {
      path: "tasks";
      method: "GET";
      query: TaskListQuery;
      response: TaskListResponse;
    }
  | {
      path: "tasks";
      method: "POST";
      body: TaskCreateBody;
      response: Task;
    }
  | {
      path: "tasks/:taskId";
      method: "GET";
      params: { taskId: string };
      response: Task;
    }
  | {
      path: "tasks/:taskId/:action";
      method: "PUT";
      params: TaskActionParams;
      response: Task;
    }
  | {
      path: "team-member-business-unit-for-pay-tracking";
      method: "GET";
      query: GetTeamMemberBusinessUnitForPayTrackingQueryParams;
      response: GetTeamMemberBusinessUnitForPayTrackingResponse[];
    }
  | {
      path: "team-member-business-unit-for-pay-tracking";
      method: "PUT";
      body: UpdateTeamMemberBusinessUnitForPayTrackingPayload;
      response: { success: boolean };
    }
  | {
      path: "dev-tools/impersonate";
      method: "POST";
      body: { email: string };
      response: { access_token: string; refresh_token: string; expires_at: number };
    }
  | {
      path: "admin/impersonate";
      method: "POST";
      body: ImpersonateTalentBody;
      response: ImpersonateTalentResponse;
    }
  | {
      path: "dev-tools/document/:id/debug-set-status";
      method: "POST";
      params: { id: string };
      body: { lifecycle_status: DocumentLifecycleStatus; review_path: "internal" | "full" };
      response: FullDocument;
    }
  | {
      path: "seed/scenarios/talent-ready-for-contract";
      method: "POST";
      body: CreateTalentReadyForContractBody;
      response: CreateTalentReadyForContractResponse;
    }
  | {
      path: "seed/scenarios/contract";
      method: "POST";
      body: CreateContractScenarioBody;
      response: CreateContractScenarioResponse;
    }
  | {
      path: "dev-tools/contracts/:contractId/simulated-signature";
      method: "POST";
      params: { contractId: string };
      body: SimulateContractSignatureBody;
      response: SimulateContractSignatureResponse;
    }
  | {
      path: "dev-tools/contracts/:contractId/simulated-refusal";
      method: "POST";
      params: { contractId: string };
      body: SimulateContractRefusalBody;
      response: SimulateContractSignatureResponse;
    }
  | {
      path: "dev-tools/contracts/:contractId/advance-steps";
      method: "GET";
      params: { contractId: string };
      response: ListContractAdvanceStepsResponse;
    }
  | {
      path: "dev-tools/contracts/:contractId/advance";
      method: "POST";
      params: { contractId: string };
      body: AdvanceContractBody;
      response: AdvanceContractResponse;
    }
  | {
      path: "seed/scenarios/personas";
      method: "POST";
      response: EnsureSeedPersonasResponse;
    }
  | {
      path: "talent/deep-research/decompose-query";
      method: "POST";
      body: TalentDeepResearchDecomposeQueryBody;
      response: TalentDeepResearchDecomposeQueryResponse;
    }
  | {
      path: "talent/deep-research/execute-query";
      method: "POST";
      body: TalentDeepResearchExecuteQueryBody;
      response: TalentDeepResearchExecuteQueryResponse;
    }
  | {
      path: "public/customer/timesheet/cancel-contact-refusal";
      method: "POST";
      query: { token: string };
      body: { timesheetWeekId: string; contractKey: number };
      response: { success: boolean };
    }
  | {
      path: "pay-tracking/import/payroll";
      method: "POST";
      body: PldDocumentImportRequest;
      response: PldDocumentImportResponse;
    }
  | {
      path: "pay-tracking/import/temporary_payroll";
      method: "POST";
      body: PldDocumentImportRequest;
      response: PldDocumentImportResponse;
    }
  | {
      path: "pay-tracking/import/certificate";
      method: "POST";
      body: PldDocumentImportRequest;
      response: PldDocumentImportResponse;
    }
  | {
      path: "pay-tracking/import/attestation";
      method: "POST";
      body: PldDocumentImportRequest;
      response: PldDocumentImportResponse;
    }
  | {
      path: "pay-tracking/import/invoice";
      method: "POST";
      body: PldDocumentImportRequest;
      response: PldDocumentImportResponse;
    }
  | {
      path: "pay-tracking/import/temporary_invoice";
      method: "POST";
      body: PldDocumentImportRequest;
      response: PldDocumentImportResponse;
    }
  | {
      path: "timesheet/:id/send-to-pld";
      method: "POST";
      params: SingleTimesheetParams;
      response: PldRhResponse;
    }
  | {
      path: "iban/extract";
      method: "POST";
      body: FormData;
      response: IbanExtractResponse;
    }
  | {
      path: "work-accidents";
      method: "GET";
      query: WorkAccidentListParamsSchema;
      response: WorkAccidentListResponseSchema;
    }
  | {
      path: "work-accidents/:id";
      method: "GET";
      params: { id: string };
      response: WorkAccidentSchema;
    }
  | {
      path: "work-accidents";
      method: "POST";
      body: FormData;
      response: WorkAccidentSchema;
    }
  | {
      path: "work-accidents/:id";
      method: "PATCH";
      params: { id: string };
      body: FormData;
      response: WorkAccidentSchema;
    }
  | {
      path: "work-accidents/:id/status";
      method: "PUT";
      params: { id: string };
      body: WorkAccidentUpdateStatusBodySchema;
      response: WorkAccidentSchema;
    }
  | {
      path: "work-accidents/:id/documents";
      method: "GET";
      params: { id: string };
      response: WorkAccidentDocumentsResponseSchema;
    }
  | {
      path: "work-accidents/:id/documents";
      method: "POST";
      params: { id: string };
      body: WorkAccidentLinkDocumentBodySchema;
      response: WorkAccidentDocumentsResponseSchema;
    }
  | {
      path: "work-accidents/:id/documents/:documentId";
      method: "DELETE";
      params: { id: string; documentId: string };
      response: WorkAccidentDocumentsResponseSchema;
    }
  | {
      path: "work-accidents/:id/documents/:documentId/files/:fileId";
      method: "DELETE";
      params: { id: string; documentId: string; fileId: string };
      response: WorkAccidentRemoveDocumentFileResponseSchema;
    }
  | {
      path: "push-marketing-requests";
      method: "POST";
      body: CreateTalentPushMarketingRequestBodySchema;
      response: CreateTalentPushMarketingRequestResponseSchema;
    }
  | {
      path: "talents/:talentId/push-marketing-requests/deactivate";
      method: "POST";
      params: { talentId: string };
      response: DeactivateTalentPushMarketingRequestsResponseSchema;
    }
  | {
      path: "medical-leaves";
      method: "GET";
      query: MedicalLeaveListParams;
      response: MedicalLeaveListResponse;
    }
  | {
      path: "medical-leaves";
      method: "POST";
      body: MedicalLeaveCreatePayload;
      response: MedicalLeave;
    }
  | {
      path: "medical-leaves/:id";
      method: "PATCH";
      params: { id: string };
      body: MedicalLeaveUpdatePayload;
      response: MedicalLeave;
    }
  | {
      path: "medical-leaves/:id";
      method: "DELETE";
      params: { id: string };
      response: { success: boolean };
    }
  | {
      path: "app/advance-payments/eligibility";
      method: "GET";
      response: AdvancePaymentEligibilityResponse;
    }
  | {
      path: "advance-payment-request";
      method: "GET";
      query: AdvancePaymentListParamsSchema;
      response: AdvancePaymentListResponseSchema;
    }
  | {
      path: "advance-payment-request";
      method: "POST";
      body: PostAdvancePaymentRequestBodySchema;
      response: PostAdvancePaymentRequestResponseSchema;
    }
  | {
      path: "advance-payment-request/:id/status";
      method: "PUT";
      params: { id: string };
      body: AdvancePaymentUpdateStatusBodySchema;
      response: AdvancePaymentSchema;
    }
  | {
      path: "advance-payment-request/:id/files";
      method: "GET";
      params: { id: string };
      response: AdvancePaymentFilesResponseSchema;
    }
  | {
      path: "advance-payment-request/:id/refuse";
      method: "PUT";
      params: { id: string };
      body: AdvancePaymentRefuseBodySchema;
      response: AdvancePaymentSchema;
    }
  | {
      path: "advance-payment-request/:id/waiting-for-hours";
      method: "POST";
      params: { id: string };
      body: EmptyBodySchema;
      response: AdvancePaymentSchema;
    }
  | {
      path: "advance-payment-request/:id/processing-resumption";
      method: "POST";
      params: { id: string };
      body: EmptyBodySchema;
      response: AdvancePaymentSchema;
    }
  | {
      path: "pld/advance-payment-request/send-to-pld";
      method: "POST";
      query: AdvancePaymentListParamsSchema;
      body: AdvancePaymentSendToPldBodySchema;
      response: PldJobTriggerResponseSchema;
    }
  | {
      path: "pld/timesheet/batch-preview";
      method: "GET";
      query: PayTrackingsTableQueryParams;
      response: PldTimesheetBatchPreviewResponseSchema;
    }
  | {
      path: "pld/timesheet/batch-send";
      method: "POST";
      query: PayTrackingsTableQueryParams;
      body: PldTimesheetBatchSendBodySchema;
      response: PldJobTriggerResponseSchema;
    }
  | {
      path: "contract/definitive-end-request";
      method: "GET";
      query: DefinitiveEndRequestListParamsSchema;
      response: DefinitiveEndRequestListResponseSchema;
    }
  | {
      path: "expense-claim-request";
      method: "GET";
      query: ExpenseClaimRequestListParamsSchema;
      response: ExpenseClaimRequestListResponseSchema;
    }
  | {
      path: "expense-claim-request";
      method: "POST";
      body: ExpenseClaimRequestCreateBodySchema;
      response: ExpenseClaimRequestCreateResponseSchema;
    }
  | {
      path: "expense-claim-request/:id/status";
      method: "PUT";
      params: { id: string };
      body: ExpenseClaimRequestUpdateStatusBodySchema;
      response: ExpenseClaimRequestSchema;
    }
  | {
      path: "expense-claim-request/:id/review";
      method: "POST";
      params: { id: string };
      body: ExpenseClaimRequestConfirmReviewBodySchema;
      response: ExpenseClaimRequestSchema;
    }
  | {
      path: "expense-claim-request/:id/files";
      method: "GET";
      params: { id: string };
      response: ExpenseClaimRequestFilesResponseSchema;
    }
  | {
      path: "independent-payment-request";
      method: "GET";
      query: IndependentPaymentRequestListParamsSchema;
      response: IndependentPaymentRequestListResponseSchema;
    }
  | {
      path: "meal-card-request";
      method: "GET";
      query: MealCardRequestListParamsSchema;
      response: MealCardRequestListResponseSchema;
    }
  | {
      path: "medical-center";
      method: "GET";
      query: MedicalCenterListParamsSchema;
      response: MedicalCenterListResponseSchema;
    }
  | {
      path: "medical-checkup-request";
      method: "GET";
      query: MedicalCheckupRequestListParamsSchema;
      response: MedicalCheckupRequestListResponseSchema;
    }
  | {
      path: "ppe-item";
      method: "GET";
      response: PpeItem[];
    }
  | {
      path: "project-typology";
      method: "GET";
      query: ProjectTypologyListParams;
      response: ProjectTypologyListResponse;
    }
  | {
      path: "region";
      method: "GET";
      query: RegionListParams;
      response: RegionListResponse;
    }
  | {
      path: "region/:regionCode";
      method: "GET";
      params: { regionCode: string };
      response: Region | null;
    }
  | {
      path: "ppe-request";
      method: "GET";
      query: PpeRequestListParamsSchema;
      response: PpeRequestListResponseSchema;
    }
  | {
      path: "professional-card-request";
      method: "GET";
      query: ProfessionalCardRequestListParamsSchema;
      response: ProfessionalCardRequestListResponseSchema;
    }
  | {
      path: "qualification-center";
      method: "GET";
      query: QualificationCenterListParamsSchema;
      response: QualificationCenterListResponseSchema;
    }
  | {
      path: "qualification-training-request";
      method: "GET";
      query: QualificationTrainingRequestListParams;
      response: QualificationTrainingRequestListResponse;
    }
  | {
      path: "talents";
      method: "GET";
      query: TalentListParams;
      response: TalentListResponse;
    }
  | {
      path: "talents/bench";
      method: "GET";
      query: TalentListParams;
      response: TalentBenchListResponse;
    }
  | {
      path: "talents/sourcing";
      method: "GET";
      query: SourcingListQuery;
      response: TalentSourcingListResponse;
    }
  | {
      path: "talent/sourcing/interpret-prompt";
      method: "POST";
      body: InterpretTalentSourcingPromptBody;
      response: InterpretTalentSourcingPromptResponse;
    }
  | {
      path: "timesheet/:contractKey/:year/:weekNumber";
      method: "GET";
      params: FindTimesheetByContractKeyParams;
      response: TimesheetResponseSchemaWithFile;
    }
  | {
      path: "permissions/contracts/:id";
      method: "GET";
      params: { id: string };
      response: TeamMemberContractPermissionsResult;
    }
  | {
      path: "permissions/expense-claim-requests/:id";
      method: "GET";
      params: { id: string };
      response: TeamMemberExpenseClaimRequestPermissionsResult;
    }
  | {
      path: "organization/find";
      method: "GET";
      query: z.infer<typeof findOrganizationParamsSchema>;
      response: z.infer<typeof organizationSchema>;
    }
  | {
      path: "organization";
      method: "GET";
      query: z.infer<typeof getOrganizationsQuerySchema>;
      response: Array<z.infer<typeof organizationSchema>>;
    }
  | {
      path: "organization/branding";
      method: "GET";
      query: GetOrganizationBrandingQuery;
      response: OrganizationBrandingResponse;
    }
  | {
      path: "organization/:id/settings";
      method: "GET";
      params: { id: string };
      response: OrganizationSettingsResponse;
    }
  | {
      path: "timesheet";
      method: "POST";
      body: CreateTimesheetByContractKeyBody;
      response: TimesheetResponse;
    }
  | {
      path: "timesheet/:id/upload-proof";
      method: "PUT";
      params: { id: string };
      body: FormData;
      response: TimesheetFile;
    }
  | {
      path: "timesheet/:id";
      method: "PUT";
      params: { id: string };
      body: UpdateTimesheetByContractKeyBody;
      response: TimesheetResponse;
    }
  | {
      path: "timesheet/:id/validate-from-admin";
      method: "PUT";
      params: { id: string };
      response: { success: boolean };
    }
  | {
      path: "pay-tracking/:id/is-ready-to-verify";
      method: "GET";
      params: IsReadyToVerifyQuery;
      response: IsReadyToVerifyResponse;
    }
  | {
      path: "timesheet/:id/download-timesheet";
      method: "GET";
      params: { id: string };
      response: Response;
    }
  | {
      path: "timesheet/:id/get-timesheet-links";
      method: "GET";
      params: { id: string };
      response: TimesheetLink[];
    }
  | {
      path: "timesheet/bulk-export-timesheet-links";
      method: "GET";
      query: BulkExportTimesheetLinksQuery;
      response: BlobResult;
    }
  | {
      path: "timesheet/:id/update-client-proposal";
      method: "PUT";
      params: { id: string };
      body: UpdateClientProposalBody;
      response: TimesheetResponse;
    }
  | {
      path: "workflows/talent-sms-reminder";
      method: "POST";
      body: object;
      response: { success: boolean };
    }
  | {
      path: "advance_payment_request/synchronize_for_loan_coverage_request";
      method: "POST";
      body: object;
      response: { success: boolean };
    }
  | {
      path: "expense-claim-request/daily-consultant-reminder";
      method: "POST";
      body: object;
      response: { success: boolean };
    }
  | {
      path: "workflows/talent-unsigned-contract-reminder";
      method: "POST";
      body: object;
      response: { success: boolean };
    }
  | {
      path: "advance_payment_request/synchronize_pending_advance_payment_requests";
      method: "POST";
      body: object;
      response: { success: boolean };
    }
  | {
      path: "workflows/talent-signup-reminder";
      method: "POST";
      body: object;
      response: { success: boolean };
    }
  | {
      path: "workflows/sync-job-offer";
      method: "POST";
      body: object;
      response: { success: boolean };
    }
  | {
      path: "workflows/medical-checkup-request-convocation-reminder";
      method: "POST";
      body: object;
      response: { success: boolean };
    }
  | {
      path: "workflows/qualification-training-request-convocation-reminder";
      method: "POST";
      body: object;
      response: { success: boolean };
    }
  | {
      path: "workflows/timesheet-not-completed-reminder";
      method: "POST";
      body: object;
      response: { success: boolean };
    }
  | {
      path: "hiring-pools/talent/:id";
      method: "GET";
      params: { id: string };
      response: HiringPool[];
    }
  | {
      path: "applications/list";
      method: "GET";
      query: ApplicationListParams;
      response: ApplicationListResponse;
    }
  | {
      path: "address-table/:id";
      method: "GET";
      params: { id: string };
      response: BaseAddress;
    }
  | {
      path: "file/:id";
      method: "GET";
      params: { id: string };
      response: BaseFile;
    }
  | {
      path: "file/:id";
      method: "DELETE";
      params: { id: string };
      response: { success: boolean };
    }
  | {
      path: "talent/:talentId/resumes";
      method: "GET";
      params: GetTalentResumeParams;
      response: ResumeFile[];
    }
  | {
      path: "address-table";
      method: "GET";
      query: AddressTableListQueryParams;
      response: AddressTableListResponse;
    }
  | {
      path: "driving-route";
      method: "GET";
      query: DrivingRouteQuery;
      response: DrivingRouteResponse;
    }
  | {
      path: "department";
      method: "GET";
      query: DepartmentListParams;
      response: DepartmentListResponse;
    }
  | {
      path: "hiring-pools/:id/talents";
      method: "POST";
      params: { id: string };
      body: AddTalentToHiringPoolBody;
      response: TalentHiringPool;
    }
  | {
      path: "hiring-pools/:id/talents/:talentId";
      method: "DELETE";
      params: RemoveTalentFromHiringPoolParams;
      response: { success: true };
    }
  | {
      path: "applications";
      method: "POST";
      body: ApplicationBody;
      response: { success: boolean };
    }
  | {
      path: "companies/:companyId/remuneration-settings";
      method: "GET";
      params: { companyId: string };
      response: { settings: CompanyRemunerationSettings | null };
    }
  | {
      path: "companies/:companyId/remuneration-settings";
      method: "PUT";
      params: { companyId: string };
      body: CompanyRemunerationSettingsUpsertPayload;
      response: CompanyRemunerationSettings;
    }
  | {
      path: "companies/:companyId/reference-documents";
      method: "GET";
      params: { companyId: string };
      response: ReferenceDocumentIdsResponse;
    }
  | {
      path: "companies/:companyId/reference-documents";
      method: "POST";
      params: { companyId: string };
      body: { fileId: string };
      response: ReferenceDocumentIdsResponse;
    }
  | {
      path: "remuneration-templates";
      method: "GET";
      query: RemunerationTemplateListParams;
      response: RemunerationTemplateListResponse;
    }
  | {
      path: "remuneration-templates";
      method: "POST";
      body: RemunerationTemplateInsertPayload;
      response: RemunerationTemplate;
    }
  | {
      path: "remuneration-templates/:id";
      method: "PATCH";
      params: { id: string };
      body: RemunerationTemplateUpdatePayload;
      response: RemunerationTemplate;
    }
  | {
      path: "remuneration-templates/:id";
      method: "DELETE";
      params: { id: string };
      response: { success: true };
    }
  | {
      path: "talent/:talentId";
      method: "PATCH";
      params: { talentId: string };
      body: UpdateTalentBody;
      response: { success: boolean };
    }
  | {
      path: "remuneration-profiles/:id";
      method: "GET";
      params: { id: string };
      response: GetRemunerationProfileResponse;
    }
  | {
      path: "contracts/modification-amendments";
      method: "POST";
      body: PostModificationAmendmentBody;
      response: PostModificationAmendmentResponse;
    }
  | {
      path: "contracts/:contractId";
      method: "GET";
      params: { contractId: string };
      response: ContractDetail;
    }
  | {
      path: "contracts/:contractId";
      method: "PATCH";
      params: { contractId: string };
      body: PatchContractBody;
      response: { success: boolean };
    }
  | {
      path: "contracts/:contractId/remuneration-profile";
      method: "PATCH";
      params: { contractId: string };
      body: PatchContractRemunerationProfileBody;
      response: PatchContractRemunerationProfileResponse;
    }
  | {
      path: "contracts/:contractId/submission";
      method: "POST";
      params: { contractId: string };
      response: ContractSubmissionResponse;
    }
  | {
      path: "contracts/:contractId/validation";
      method: "POST";
      params: { contractId: string };
      response: ContractStatusMutationResponse;
    }
  | {
      path: "contracts/:contractId/sending";
      method: "POST";
      params: { contractId: string };
      response: ContractStatusMutationResponse;
    }
  | {
      path: "contracts/:contractId/signature";
      method: "POST";
      params: { contractId: string };
      response: ContractStatusMutationResponse;
    }
  | {
      path: "contracts/:contractId/amendment-validation";
      method: "POST";
      params: { contractId: string };
      response: ContractStatusMutationResponse;
    }
  | {
      path: "contracts/:contractId/processing";
      method: "POST";
      params: { contractId: string };
      response: ContractStatusMutationResponse;
    }
  | {
      path: "contracts/:contractId/contacts";
      method: "GET";
      params: { contractId: string };
      response: ContractContactsResponse;
    }
  | {
      path: "contracts/:contractId/contacts/:type";
      method: "POST";
      params: AddContractContactsParams;
      body: AddContractContactsBody;
      response: ContractContactsMutationResponse;
    }
  | {
      path: "contracts/:contractId/contacts/:type/:contactId";
      method: "DELETE";
      params: RemoveContractContactParams;
      response: ContractContactsMutationResponse;
    }
  | {
      path: "contracts/:contractId/contacts-copy";
      method: "POST";
      params: ContractParams;
      body: CopyContractContactsBody;
      response: ContractContactsMutationResponse;
    }
  | {
      path: "contracts/:contractId/owner";
      method: "PUT";
      params: { contractId: string };
      body: ChangeContractOwnerBody;
      response: ContractOwnerMutationResponse;
    }
  | {
      path: "contracts/:contractId/definitive-end/processing";
      method: "POST";
      params: { contractId: string };
      response: ContractDefinitiveEndMutationResponse;
    }
  | {
      path: "contracts/:contractId/definitive-end";
      method: "DELETE";
      params: { contractId: string };
      response: ContractDefinitiveEndMutationResponse;
    }
  | {
      path: "contracts/:contractId/documents";
      method: "GET";
      params: { contractId: string };
      query: ContractDocumentsQueryParams;
      response: ContractDocument[];
    }
  | {
      path: "contracts/:contractId/documents";
      method: "POST";
      params: { contractId: string };
      body: LinkContractDocumentBody;
      response: LinkContractDocumentResponse;
    }
  | {
      path: "contracts/:contractId/correction-request";
      method: "POST";
      params: { contractId: string };
      body: ContractCorrectionRequestBody;
      response: ContractStatusMutationResponse;
    }
  | {
      path: "contracts/:contractId/validation-change-request";
      method: "POST";
      params: { contractId: string };
      body: ContractCorrectionRequestBody;
      response: ContractStatusMutationResponse;
    }
  | {
      path: "contracts/:contractId/processing-precision-request";
      method: "POST";
      params: { contractId: string };
      body: ContractCorrectionRequestBody;
      response: ContractStatusMutationResponse;
    }
  | {
      path: "contracts/extension-amendments";
      method: "POST";
      body: PostExtensionAmendmentBody;
      response: PostExtensionAmendmentResponse;
    }
  | {
      path: "talent/:talentId/referencing";
      method: "PATCH";
      params: { talentId: string };
      body: ReferencingTalentBody;
      response: BaseTalent;
    }
  | {
      path: "contract/:contractId/definitive-end-request";
      method: "PATCH";
      params: { contractId: string };
      body: ContractDefinitiveEndRequestBody;
      response: ContractSchema;
    }
  | {
      path: "talent/:talentId/substitute-bench";
      method: "PATCH";
      params: { talentId: string };
      body: TalentSubstituteBenchBody;
      response: BaseTalent;
    }
  | {
      path: "talent/:talentId/block-advance-payment";
      method: "PATCH";
      params: { talentId: string };
      body: BlockAdvancePaymentBody;
      response: BaseTalent;
    }
  | {
      path: "talents/blocked-advance-payment";
      method: "GET";
      response: BlockedAdvancePaymentTalent[];
    }
  | {
      path: "talent/:talentId/resume-data";
      method: "GET";
      params: { talentId: string };
      response: TalentResumeDataResponse;
    }
  | {
      path: "talent/:talentId/resume-data";
      method: "PATCH";
      params: { talentId: string };
      body: UpdateTalentResumeDataBody;
      response: UpdateTalentResumeResponse;
    }
  | {
      path: "talent/:talentId/activity-feed";
      method: "GET";
      params: { talentId: string };
      response: TalentActivityListResponse;
    }
  | {
      path: "contracts";
      method: "POST";
      body: FormData;
      response: PostContractResponse;
    }
  | {
      path: "contracts/with-auto-mission";
      method: "POST";
      body: PostContractWithAutoMissionBody;
      response: PostContractWithAutoMissionResponse;
    }
  | {
      path: "business-unit";
      method: "GET";
      query: BusinessUnitListParams;
      response: BusinessUnitListResponse;
    }
  | {
      path: "business-unit/:id";
      method: "GET";
      params: BusinessUnitIdParams;
      response: BusinessUnitGetResponse;
    }
  | {
      path: "talent/:id/business-units";
      method: "POST";
      params: LinkTalentToMissionBusinessUnitParams;
      body: LinkTalentToMissionBusinessUnitBody;
      response: TalentBusinessUnit;
    }
  | {
      path: "qualification-training-request/:id";
      method: "GET";
      params: { id: string };
      response: QualificationTrainingRequest;
    }
  | {
      path: "qualification-training-request/:id";
      method: "PATCH";
      params: { id: string };
      body: QualificationTrainingRequestUpdate;
      response: QualificationTrainingRequest;
    }
  | {
      path: "qualification-training-request/documents";
      method: "GET";
      query: QualificationTrainingRequestDocumentsListParams;
      response: QualificationTrainingRequestDocumentResponse[];
    }
  | {
      path: "qualification-training-request/:qualificationId/documents/:documentId";
      method: "PATCH";
      params: QualificationTrainingRequestDocumentUpdateParams;
      body: QualificationTrainingRequestDocumentUpdate;
      response: QualificationTrainingRequest;
    }
  | {
      path: "qualification-training-request";
      method: "POST";
      body: CreateQualificationTrainingRequest;
      response: QualificationTrainingRequest;
    }
  | {
      path: "calls/ongoing-for-me";
      method: "GET";
      response: OngoingCallTalent;
    }
  | {
      path: "calls/ongoing/:talentId";
      method: "GET";
      params: { talentId: string };
      response: OngoingCall;
    }
  | {
      path: "dev/simulated-sipsim-calls";
      method: "POST";
      body: StartSimulatedSipsimCallBody;
      response: SimulatedSipsimCall;
    }
  | {
      path: "dev/simulated-sipsim-calls/:pbxId/end";
      method: "POST";
      params: SimulatedSipsimCallParams;
      body: EndSimulatedSipsimCallBody;
      response: SimulatedSipsimCall;
    }
  | {
      path: "calls/:callId";
      method: "GET";
      params: { callId: string };
      response: CallSchema;
    }
  | {
      path: "calls/:callId";
      method: "PATCH";
      params: { callId: string };
      body: UpdateCallPayload;
      response: CallSchema;
    }
  // TEMPORARY — playground SipSim proxy endpoints; remove when production endpoints land
  | {
      path: "playground/sipsim/calls/:externalCallId/transcription";
      method: "GET";
      params: { externalCallId: string };
      response: CallTranscriptionSchema;
    }
  | {
      path: "playground/sipsim/calls/:externalCallId/summary";
      method: "GET";
      params: { externalCallId: string };
      response: CallSummarySchema;
    }
  | {
      path: "playground/sipsim/calls/:externalCallId/summary";
      method: "POST";
      params: { externalCallId: string };
      response: CallSummarySchema | null;
    }
  | {
      path: "playground/sipsim/calls/:externalCallId/summary";
      method: "PATCH";
      params: { externalCallId: string };
      body: PatchCallSummaryInstructionsPayload;
      response: CallSummarySchema;
    }
  | {
      path: "playground/sipsim/settings/summary";
      method: "GET";
      response: CallSummarySettingsSchema;
    }
  | {
      path: "contracts/transmission-type";
      method: "GET";
      query: { contractKeys: number[] };
      response: ContractKeyTransmissionType[];
    }
  | {
      path: "talent/:talentId/address";
      method: "GET";
      params: { talentId: string };
      response: BaseAddress | null;
    }
  | {
      path: "talent/:id/rib";
      method: "GET";
      params: { id: string };
      response: TalentRibSchema | null;
    }
  | {
      path: "contracts/planning";
      method: "GET";
      query: { contract_key: number };
      response: ContractKeyDetail;
    };

export type ApiEndpointMethod<Path extends ApiEndpoint["path"]> = Extract<ApiEndpoint, { path: Path }>["method"];
export type ApiEndpointResponse<Path extends ApiEndpoint["path"], Method extends ApiEndpoint["method"]> = Extract<
  ApiEndpoint,
  { path: Path; method: Method }
>["response"];
export type ApiEndpointBody<Path extends ApiEndpoint["path"], Method extends ApiEndpoint["method"]> =
  Extract<ApiEndpoint, { path: Path; method: Method }> extends { body: infer Body } ? Body : undefined;
export type ApiEndpointQuery<Path extends ApiEndpoint["path"], Method extends ApiEndpoint["method"]> =
  Extract<ApiEndpoint, { path: Path; method: Method }> extends { query: infer Query } ? Query : undefined;

// Ensure params values are strings
type StringifyParams<T> = {
  [K in keyof T]: string;
};

export type ApiEndpointParams<Path extends ApiEndpoint["path"], Method extends ApiEndpoint["method"]> =
  Extract<ApiEndpoint, { path: Path; method: Method }> extends { params: infer Params }
    ? StringifyParams<Params>
    : undefined;

export type ApiEndpointOutput<
  Path extends ApiEndpoint["path"],
  Method extends ApiEndpoint["method"] = ApiEndpointMethod<Path>,
  Data = ApiEndpointResponse<Path, Method>,
> =
  | {
      success: true;
      data: Data;
      status: number;
    }
  | { success: false; error: ErrorSerialized; status: number };

export type BlobResult = {
  blob: Blob;
  filename: string;
  mimetype: string;
  file: File;
};

export type ApiEndpointBlobOutput =
  { success: true; data: BlobResult; status: number } | { success: false; error: ErrorSerialized; status: number };

export type ApiInvokeOptions<
  Path extends ApiEndpoint["path"],
  Method extends ApiEndpoint["method"] = ApiEndpointMethod<Path>,
> = {
  path: Path;
  method: Method;
  body?: ApiEndpointBody<Path, Method>;
  query?: ApiEndpointQuery<Path, Method>;
  params?: ApiEndpointParams<Path, Method>;
  maybeNull?: boolean;
  responseType?: "json" | "blob";
  signal?: AbortSignal;
  headers?: HeadersInit;
};

function serializeUrlValue(value: unknown): string {
  if (Array.isArray(value)) return value.map(serializeUrlValue).join(",");
  if (typeof value === "string" || typeof value === "number" || typeof value === "boolean") return String(value);
  throw new SharedError(`[ApiClient] Cannot serialize ${typeof value} into a URL`);
}

function isErrorSerialized(value: unknown): value is ErrorSerialized {
  if (typeof value !== "object" || value === null) return false;
  if (!("message" in value) || typeof value.message !== "string") return false;
  return !("clientMessage" in value) || value.clientMessage === undefined || typeof value.clientMessage === "string";
}

function parseFilenameFromDisposition(disposition: string): string | null {
  const match = disposition.match(/filename="?(.+?)"?(?:;|$)/);
  return match ? match[1] : null;
}

export class ApiClient {
  constructor(
    private readonly params: {
      apiUrl: string;
      accessToken: string;
      logger: Logger;
    }
  ) {}

  public invoke<Path extends ApiEndpoint["path"], Method extends ApiEndpoint["method"] = ApiEndpointMethod<Path>>(
    options: ApiInvokeOptions<Path, Method> & { responseType: "blob" }
  ): Promise<ApiEndpointBlobOutput>;
  public invoke<
    Path extends ApiEndpoint["path"],
    Method extends ApiEndpoint["method"] = ApiEndpointMethod<Path>,
    Data = ApiEndpointResponse<Path, Method>,
  >(options: ApiInvokeOptions<Path, Method>): Promise<ApiEndpointOutput<Path, Method, Data>>;
  public async invoke<
    Path extends ApiEndpoint["path"],
    Method extends ApiEndpoint["method"] = ApiEndpointMethod<Path>,
    Data = ApiEndpointResponse<Path, Method>,
  >(options: ApiInvokeOptions<Path, Method>): Promise<ApiEndpointOutput<Path, Method, Data> | ApiEndpointBlobOutput> {
    let status: number | undefined;
    try {
      const { apiUrl, accessToken, logger } = this.params;

      if (!apiUrl) throw new SharedError("[ApiClient] Missing API URL");
      if (!accessToken) throw new SharedError("[ApiClient] Missing access token");

      let url = `${apiUrl}/${options.path}`;
      if (options.query) {
        // We need to filter out undefined and null values because zod will sometimes return them
        const searchParams = new URLSearchParams();
        for (const [key, value] of Object.entries(options.query)) {
          if (value !== undefined && value !== null && value !== "") searchParams.append(key, serializeUrlValue(value));
        }
        const queryString = searchParams.toString();
        if (queryString) url += `?${queryString}`;
      }
      if (options.params) {
        Object.entries(options.params).forEach(([key, value]) => {
          url = url.replace(new RegExp(`:${key}`, "g"), serializeUrlValue(value));
        });
      }

      const headers: HeadersInit = {
        Authorization: `Bearer ${accessToken}`,
      };

      let body: BodyInit | undefined;
      if (options.body) {
        if (options.body instanceof FormData) {
          // Don't set Content-Type for FormData, let the browser set it with boundary
          body = options.body;
        } else {
          headers["Content-Type"] = "application/json";
          body = JSON.stringify(options.body);
        }
      }

      const response = await fetch(url, {
        method: options.method,
        headers,
        signal: options.signal,
        body,
      });

      status = response.status;

      if (!response.ok) {
        const errorBody: unknown = await response.json();
        if (!isErrorSerialized(errorBody)) throw new Error(`Request failed with status ${response.status}`);
        logger.error(errorBody.message, { err: errorBody });
        throw new Error(errorBody.clientMessage || errorBody.message);
      }

      if (options.responseType === "blob") {
        const blob = await response.blob();
        const disposition = response.headers.get("Content-Disposition");
        if (!disposition) throw new Error("Missing Content-Disposition header");
        const filename = parseFilenameFromDisposition(disposition);
        if (!filename) throw new Error("Missing filename in Content-Disposition");
        const mimetype = response.headers.get("Content-Type");
        if (!mimetype) throw new Error("Missing Content-Type header");
        const file = new File([blob], filename, { type: mimetype });
        return { success: true, data: { blob, filename, mimetype, file }, status };
      }

      const data: Data = await response.json();
      if (!options.maybeNull && !data) throw new Error("No data returned");

      return { success: true, data, status };
    } catch (exception) {
      if (!options.signal?.aborted) {
        this.params.logger.error("API request failed", { err: exception });
      }

      return {
        success: false,
        status: status ?? 500,
        error: {
          message: getErrorMessageForClient(exception),
        },
      };
    }
  }
}
